import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { APP_ID, READINGS_BUCKET, ADMIN_EMAIL, PUBLIC_READINGS_URL_BASE, FIREBASE_STORAGE_BASE_PATH, MOCK_GROUPS } from './constants';
import { ArchiveFile, DiscussionThread, DBRow, Group, Post, SearchResult } from './types';
import Sidebar from './components/Sidebar';
import { ArchiveGrid } from './components/ArchiveGrid';
import ReadingView from './components/ReadingView';
import LegacyPage from './components/LegacyPage';
import CommunityPage from './components/CommunityPage';
import ContactPage from './components/ContactPage';
import ReadingsBrowser from './components/ReadingsBrowser';
import WikiHeader from './components/WikiHeader';
import SettingsPage from './components/SettingsPage';
import ProfileCollection from './components/ProfileCollection';
import ProfileCategory from './components/ProfileCategory';
import ProfileWiki from './components/ProfileWiki';
import ChatPage from './components/ChatPage';
import { EditorPage, EditorRef } from './components/EditorPage';
import { signInWithGoogle, signInWithEmail, logout, onAuthStateChange, checkRedirectResult } from './services/auth';
import { 
  onThreadsUpdate, 
  saveThreadToFirestore, 
  deleteThreadFromFirestore, 
  subscribeToWikiArticles, 
  saveWikiArticle, 
  onUserSettingsUpdate, 
  saveUserSettings, 
  getUserSettings,
  deleteWikiArticle, 
  onPageContentUpdate, 
  updatePageContent,
  onBookmarksUpdate,
  saveBookmark,
  deleteBookmark,
  onBookmarkCategoriesUpdate,
  saveBookmarkCategory,
  deleteBookmarkCategory,
  onInvoicesUpdate,
  onPaymentsUpdate,
  Invoice,
  PaymentRecord,
  savePaymentRecord,
  updateInvoiceStatus,
  saveAppRegistration,
  saveInvoice,
  onCommunityPostsUpdate,
  saveCommunityPost,
  deleteCommunityPost,
  onAiUsageUpdate,
  saveAiUsage
} from './services/firestoreService';
import { Bookmark, BookmarkCategory } from './types';
import BookmarkDialog from './components/BookmarkDialog';
import PaymentMethodDialog from './components/PaymentMethodDialog';
import LanguageSelector from './components/LanguageSelector';
import { loadFilesFromCache, saveFilesToCache } from './services/db';
import { getMockFiles, deleteMockFile } from './services/mockStorage';
import { getFallbackReadings, getFallbackReadingContent } from './services/fallbackReadings';
import { fileToBase64, updateDynamicFavicons, uploadMedia, getResizedImageUrl } from './services/storageService';
import { onInstallAvailabilityChange, installPWA, isStandalone, requestPersistence } from './services/pwaService';

import IOSInstallPrompt from './components/IOSInstallPrompt';
import AuthModal from './components/AuthModal';

interface User {
  uid: string;
  name: string;
  email: string;
  picture: string;
  monthlySpendCap?: number;
  topupBalance?: number;
  currentUsage?: number;
  aiUsage?: {
    dailyFreeUsed: number;
    freeLimit: number;
    credits: number;
  };
  hasPaymentMethod?: boolean;
  primaryPaymentMethod?: 'stripe' | 'google-pay' | null;
  notifications?: {
    pushEnabled: boolean;
    emailEnabled: boolean;
    newArticle: boolean;
    newFeedPost: boolean;
    newFeedPostInGroup: boolean;
    commentsOnMyPost: boolean;
    responsesToMyComment: boolean;
  };
}

const DEFAULT_NOTIFICATIONS = {
  pushEnabled: true,
  emailEnabled: true,
  newArticle: true,
  newFeedPost: true,
  newFeedPostInGroup: true,
  commentsOnMyPost: true,
  responsesToMyComment: true
};

type Page = 'legacy' | 'wiki' | 'chat' | 'readings' | 'community' | 'profile' | 'settings';

const LANGUAGES = [
    { code: 'af', name: 'Afrikaans', flag: '🇿🇦' },
    { code: 'sq', name: 'Albanian', flag: '🇦🇱' },
    { code: 'am', name: 'Amharic', flag: '🇪🇹' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hy', name: 'Armenian', flag: '🇦🇲' },
    { code: 'az', name: 'Azerbaijani', flag: '🇦🇿' },
    { code: 'eu', name: 'Basque', flag: '🇪🇸' },
    { code: 'be', name: 'Belarusian', flag: '🇧🇾' },
    { code: 'bn', name: 'Bengali', flag: '🇧🇩' },
    { code: 'bs', name: 'Bosnian', flag: '🇧🇦' },
    { code: 'bg', name: 'Bulgarian', flag: '🇧🇬' },
    { code: 'ca', name: 'Catalan', flag: '🇪🇸' },
    { code: 'ceb', name: 'Cebuano', flag: '🇵🇭' },
    { code: 'ny', name: 'Chichewa', flag: '🇲🇼' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
    { code: 'zh-TW', name: 'Chinese (Traditional)', flag: '🇹🇼' },
    { code: 'co', name: 'Corsican', flag: '🇫🇷' },
    { code: 'hr', name: 'Croatian', flag: '🇭🇷' },
    { code: 'cs', name: 'Czech', flag: '🇨🇿' },
    { code: 'da', name: 'Danish', flag: '🇩🇰' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'eo', name: 'Esperanto', flag: '🌍' },
    { code: 'et', name: 'Estonian', flag: '🇪🇪' },
    { code: 'tl', name: 'Filipino', flag: '🇵🇭' },
    { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'fy', name: 'Frisian', flag: '🇳🇱' },
    { code: 'gl', name: 'Galician', flag: '🇪🇸' },
    { code: 'ka', name: 'Georgian', flag: '🇬🇪' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'el', name: 'Greek', flag: '🇬🇷' },
    { code: 'gu', name: 'Gujarati', flag: '🇮🇳' },
    { code: 'ht', name: 'Haitian Creole', flag: '🇭🇹' },
    { code: 'ha', name: 'Hausa', flag: '🇳🇬' },
    { code: 'haw', name: 'Hawaiian', flag: '🇺🇸' },
    { code: 'iw', name: 'Hebrew', flag: '🇮🇱' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'hmn', name: 'Hmong', flag: '🌍' },
    { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
    { code: 'is', name: 'Icelandic', flag: '🇮🇸' },
    { code: 'ig', name: 'Igbo', flag: '🇳🇬' },
    { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
    { code: 'ga', name: 'Irish', flag: '🇮🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'jw', name: 'Javanese', flag: '🇮🇩' },
    { code: 'kn', name: 'Kannada', flag: '🇮🇳' },
    { code: 'kk', name: 'Kazakh', flag: '🇰🇿' },
    { code: 'km', name: 'Khmer', flag: '🇰🇭' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'ku', name: 'Kurdish (Kurmanji)', flag: '🌍' },
    { code: 'ky', name: 'Kyrgyz', flag: '🇰🇬' },
    { code: 'lo', name: 'Lao', flag: '🇱🇦' },
    { code: 'la', name: 'Latin', flag: '🏛️' },
    { code: 'lv', name: 'Latvian', flag: '🇱🇻' },
    { code: 'lt', name: 'Lithuanian', flag: '🇱🇹' },
    { code: 'lb', name: 'Luxembourgish', flag: '🇱🇺' },
    { code: 'mk', name: 'Macedonian', flag: '🇲🇰' },
    { code: 'mg', name: 'Malagasy', flag: '🇲🇬' },
    { code: 'ms', name: 'Malay', flag: '🇲🇾' },
    { code: 'ml', name: 'Malayalam', flag: '🇮🇳' },
    { code: 'mt', name: 'Maltese', flag: '🇲🇹' },
    { code: 'mi', name: 'Maori', flag: '🇳🇿' },
    { code: 'mr', name: 'Marathi', flag: '🇮🇳' },
    { code: 'mn', name: 'Mongolian', flag: '🇲🇳' },
    { code: 'my', name: 'Myanmar (Burmese)', flag: '🇲🇲' },
    { code: 'ne', name: 'Nepali', flag: '🇳🇵' },
    { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
    { code: 'or', name: 'Odia', flag: '🇮🇳' },
    { code: 'ps', name: 'Pashto', flag: '🇦🇫' },
    { code: 'fa', name: 'Persian', flag: '🇮🇷' },
    { code: 'pl', name: 'Polish', flag: '🇵🇱' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'pa', name: 'Punjabi', flag: '🇮🇳' },
    { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'sm', name: 'Samoan', flag: '🇼🇸' },
    { code: 'gd', name: 'Scots Gaelic', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
    { code: 'sr', name: 'Serbian', flag: '🇷🇸' },
    { code: 'st', name: 'Sesotho', flag: '🇿🇦' },
    { code: 'sn', name: 'Shona', flag: '🇿🇼' },
    { code: 'sd', name: 'Sindhi', flag: '🇵🇰' },
    { code: 'si', name: 'Sinhala', flag: '🇱🇰' },
    { code: 'sk', name: 'Slovak', flag: '🇸🇰' },
    { code: 'sl', name: 'Slovenian', flag: '🇸🇮' },
    { code: 'so', name: 'Somali', flag: '🇸🇴' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'su', name: 'Sundanese', flag: '🇮🇩' },
    { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
    { code: 'tg', name: 'Tajik', flag: '🇹🇯' },
    { code: 'ta', name: 'Tamil', flag: '🇮🇳' },
    { code: 'te', name: 'Telugu', flag: '🇮🇳' },
    { code: 'th', name: 'Thai', flag: '🇹🇭' },
    { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
    { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
    { code: 'ur', name: 'Urdu', flag: '🇵🇰' },
    { code: 'uz', name: 'Uzbek', flag: '🇺🇿' },
    { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
    { code: 'cy', name: 'Welsh', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
    { code: 'xh', name: 'Xhosa', flag: '🇿🇦' },
    { code: 'yi', name: 'Yiddish', flag: '🌍' },
    { code: 'yo', name: 'Yoruba', flag: '🇳🇬' },
    { code: 'zu', name: 'Zulu', flag: '🇿🇦' }
];

const ReadingsHeaderControls: React.FC<{
    searchTerm: string;
    onSearchChange: (val: string) => void;
    isIndexing: boolean;
    indexedCount: number;
    totalCount: number;
}> = ({ searchTerm, onSearchChange }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);
    const [localQuery, setLocalQuery] = useState(searchTerm);

    // Keep localQuery in sync with props when searchTerm updates externally (e.g., cleared)
    useEffect(() => {
        setLocalQuery(searchTerm);
    }, [searchTerm]);

    // Debounce the state changes from typing to parent onSearchChange
    useEffect(() => {
        const handler = setTimeout(() => {
            if (localQuery !== searchTerm) {
                onSearchChange(localQuery);
            }
        }, 300); // 300ms debounce
        return () => clearTimeout(handler);
    }, [localQuery, onSearchChange, searchTerm]);

    useEffect(() => {
        if (isExpanded && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isExpanded]);

    return (
        <div className={`flex items-center gap-2 ${isExpanded ? 'w-full md:w-auto' : ''}`}>
            <div className={`transition-all duration-300 ${isExpanded ? 'absolute inset-0 z-50 bg-[#f2efe4] dark:bg-[#0f1521] px-4 flex items-center md:relative md:inset-auto md:bg-transparent md:px-0 md:w-96 w-full' : 'relative flex items-center w-auto'}`}>
                
                {isExpanded && (
                    <button 
                        onClick={() => { setIsExpanded(false); if(localQuery) { setLocalQuery(''); onSearchChange(''); } }}
                        className="w-8 h-8 flex items-center justify-center text-stone-500 mr-2 md:hidden shrink-0"
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                    </button>
                )}

                {!isExpanded && (
                    <button 
                        onClick={() => setIsExpanded(true)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800 text-stone-500 hover:text-amber-500 shadow-sm"
                    >
                        <i className="fa-solid fa-magnifying-glass"></i>
                    </button>
                )}

                {isExpanded && (
                    <div className="relative w-full animate-in fade-in zoom-in-95 duration-200">
                        <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                        <input 
                            ref={inputRef}
                            type="text" 
                            autoFocus
                            placeholder="Search records (e.g. 'motor 4666')..." 
                            value={localQuery}
                            onChange={(e) => setLocalQuery(e.target.value)}
                            className="w-full pl-8 pr-8 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-full text-xs outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 shadow-inner transition-all"
                        />
                        <button 
                            onClick={() => { 
                                if (localQuery) {
                                    setLocalQuery('');
                                    onSearchChange('');
                                } else {
                                    setIsExpanded(false);
                                }
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600 hidden md:block"
                        >
                            <i className="fa-solid fa-xmark text-xs"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const App: React.FC = () => {
  const [activePage, setActivePage] = useState<Page>('legacy');
  const [monthlySpendCap, setMonthlySpendCap] = useState<number>(0);
  const [topupBalance, setTopupBalance] = useState<number>(0);
  const [currentUsage, setCurrentUsage] = useState<number>(0.00);
  const [guestUsage, setGuestUsage] = useState<number>(() => {
      const stored = localStorage.getItem('guestUsage');
      return stored ? parseFloat(stored) : 0;
  });

  useEffect(() => {
    localStorage.setItem('guestUsage', guestUsage.toString());
  }, [guestUsage]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [wikiSearchTerm, setWikiSearchTerm] = useState('');
  const [communitySearchTerm, setCommunitySearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState<ArchiveFile | null>(null);
  const [readingHighlight, setReadingHighlight] = useState<string>('');
  const [readingsQuery, setReadingsQuery] = useState<string>('');
  const [chatInitialQuery, setChatInitialQuery] = useState<string>('');
  
  const [isInstallable, setIsInstallable] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [showIOSInstallModal, setShowIOSInstallModal] = useState(false);
  
  const [urlError, setUrlError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<{ message: string; domain?: string; type?: 'firebase' | 'gcp' } | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'options' | 'login' | 'signup' | 'reset' | 'confirmReset' | 'directChange'>('options');
  const [authResetToken, setAuthResetToken] = useState<string>('');
  
  // Check for password reset tokens in URL query params on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    const tokenParam = params.get('token') || params.get('oobCode');

    if ((modeParam === 'customReset' || modeParam === 'resetPassword') && tokenParam) {
      setAuthModalMode('confirmReset');
      setAuthResetToken(tokenParam);
      setIsAuthModalOpen(true);

      // Clean query params from URL so refreshing won't re-trigger
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('mode');
      cleanUrl.searchParams.delete('token');
      cleanUrl.searchParams.delete('oobCode');
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }, []);
  
  const [user, setUser] = useState<User | null>(null);
  const [aiUsage, setAiUsage] = useState<{ dailyFreeUsed: number; freeLimit: number; credits: number }>({
    dailyFreeUsed: 0,
    freeLimit: 10,
    credits: 0
  });
  const userRef = useRef<User | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    const unsub = onCommunityPostsUpdate(setCommunityPosts);
    return () => unsub();
  }, []);

  const handleSavePost = useCallback(async (post: Post) => {
    return saveCommunityPost(post);
  }, []);

  const handleDeletePost = useCallback(async (postId: string) => {
    return deleteCommunityPost(postId);
  }, []);

  
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [bypassBilling, setBypassBilling] = useState<boolean>(() => {
    const saved = localStorage.getItem('cayce_bypass_billing');
    return saved !== null ? saved === 'true' : true;
  });

  const handleBypassBillingChange = (val: boolean) => {
    setBypassBilling(val);
    localStorage.setItem('cayce_bypass_billing', String(val));
  };
  const [pageContent, setPageContent] = useState<Record<string, string>>({});
  const [isPageContentLoaded, setIsPageContentLoaded] = useState(false); 
  
  const [threads, setThreads] = useState<DiscussionThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>();
  const [groups, setGroups] = useState<Group[]>(MOCK_GROUPS);
  const [communityFilter, setCommunityFilter] = useState<'All' | 'Update' | 'Question' | 'Insight' | 'Media'>('All');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [communityPosts, setCommunityPosts] = useState<Post[]>([]); 
  const [forceShowContact, setForceShowContact] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  
  const [sidebarExpandedItems, setSidebarExpandedItems] = useState<Set<string>>(new Set());
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState('');


  const localDB = useRef<Map<string, DBRow>>(new Map());
  const [bucketFiles, setBucketFiles] = useState<ArchiveFile[]>([]);
  const [isLoadingBucket, setIsLoadingBucket] = useState(true);
  const [isWikiLoaded, setIsWikiLoaded] = useState(false); 
  const [indexedCount, setIndexedCount] = useState(0);
  const [memoryUsageMB, setMemoryUsageMB] = useState(0);

  useEffect(() => {
    if (bucketFiles.length > 0) {
      setIndexedCount(bucketFiles.length);
    }
  }, [bucketFiles]);
  const [firestoreArticles, setFirestoreArticles] = useState<ArchiveFile[]>([]);
  const [mockVersion, setMockVersion] = useState(0);
  const [deletedFileIds, setDeletedFileIds] = useState<Set<string>>(new Set());
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [fontSize, setFontSize] = useState<'sm' | 'base' | 'lg'>('base');
  const [payAsYouGo, setPayAsYouGo] = useState(false);
  const [quotaAlertThreshold, setQuotaAlertThreshold] = useState(5.00); 
  const [currentLangCode, setCurrentLangCode] = useState(() => 'en');
  const [urlSearchParams, setUrlSearchParams] = useState(new URLSearchParams());
  const [currentPathname, setCurrentPathname] = useState('/');
  const [currentHash, setCurrentHash] = useState('');
  const lastSyncedLanguage = useRef<string | null>(null);
  const isSettingsInitialized = useRef(false);
  const [isIndexing, setIsIndexing] = useState(false);
  const indexAbortController = useRef<AbortController | null>(null);
  const redirectChecked = useRef(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMediaUrlInput, setLogoMediaUrlInput] = useState('');
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isPaymentMethodDialogOpen, setIsPaymentMethodDialogOpen] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<{ run: () => void } | null>(null);
  const editorRef = useRef<EditorRef>(null);

  const renderLanguageSelector = () => (
    <LanguageSelector 
        languages={LANGUAGES} 
        currentLangCode={currentLangCode} 
        onSelect={handleLanguageSelect} 
    />
  );

  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [bookmarkCategories, setBookmarkCategories] = useState<BookmarkCategory[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);

  const isIOS = useMemo(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }, []);

  useEffect(() => {
    if (activePage === 'chat' && user && !user.hasPaymentMethod && !bypassBilling) {
       const hasSeen = localStorage.getItem('hasSeenPaymentPrompt');
       if (!hasSeen) {
         setIsPaymentMethodDialogOpen(true);
       }
    }
  }, [activePage, user, bypassBilling]);

  const isStudio = useMemo(() => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true; 
    }
  }, []);

  const handleClosePaymentMethodDialog = () => {
      localStorage.setItem('hasSeenPaymentPrompt', 'true');
      setIsPaymentMethodDialogOpen(false);
  };

  const onNavigateToSettings = (hash: string = 'payment-methods') => {
      localStorage.setItem('hasSeenPaymentPrompt', 'true');
      setIsPaymentMethodDialogOpen(false);
      navigateInternal('/settings', { hash });
  };

  const handleInstallClick = async () => {
      if (isIOS) {
          setShowIOSInstallModal(true);
          return;
      } 
      
      const installed = await installPWA();
      
      if (installed) {
          setIsInstallable(false);
      } else {
          if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
              alert("Installation failed: PWA installation requires a secure HTTPS connection.");
          } else {
              alert("The browser is not allowing the installation prompt right now.\n\n1. Look for the 'App Available' icon in your URL bar (usually on the right).\n2. Or check the browser menu (three dots) -> 'Install App'.");
          }
      }
  };

  useEffect(() => {
    const root = document.documentElement;
    if (!root.style.transition.includes('font-size')) {
        const currentTransition = root.style.transition;
        root.style.transition = currentTransition 
            ? `${currentTransition}, font-size 0.3s cubic-bezier(0.4, 0, 0.2, 1)` 
            : 'font-size 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    }

    if (fontSize === 'sm') {
        root.style.fontSize = '14px';
    } else if (fontSize === 'lg') {
        root.style.fontSize = '18px';
    } else {
        root.style.fontSize = '16px';
    }
  }, [fontSize]);

  useEffect(() => {
    const unsubscribePWA = onInstallAvailabilityChange((available) => {
        setIsInstallable(available);
    });
    
    if (isStandalone()) {
        requestPersistence();
    }

    const checkPWA = () => setIsPWA(isStandalone());
    checkPWA();
    
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleMediaChange = (e: MediaQueryListEvent) => setIsPWA(e.matches);
    try {
        mediaQuery.addEventListener('change', handleMediaChange);
    } catch (e) {
        mediaQuery.addListener(handleMediaChange);
    }

    let refreshing = false;
    const listener = () => {
        if (refreshing) return;
        // Only reload if there was already a controller (i.e., this is an update, not the first install)
        if (!navigator.serviceWorker.controller) return;
        
        // Disable auto-reload in AI Studio preview to prevent refresh loops
        try {
            if (window.self !== window.top) {
                console.log("App: Service Worker updated, but skipping reload in AI Studio preview.");
                return;
            }
        } catch (e) {
            // If we can't check (CORS), assume it's a frame and skip reload
            return;
        }

        refreshing = true;
        console.log("App: Service Worker updated. Skipping auto-reload to prevent infinite loops.");
        // window.location.reload();
    };
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', listener);
    }
    
    return () => {
        unsubscribePWA();
        try { mediaQuery.removeEventListener('change', handleMediaChange); } catch(e) { mediaQuery.removeListener(handleMediaChange); }
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.removeEventListener('controllerchange', listener);
        }
    };
  }, []);

  useEffect(() => {
    // Initial load from localStorage as a fallback/hint
    const localPref = localStorage.getItem('user_lang_preference');
    if (localPref && localPref !== 'en') {
      setCurrentLangCode(localPref);
      lastSyncedLanguage.current = localPref;
      setGoogleTranslateCookie(localPref);
    }

    const match = document.cookie.match(/googtrans=\/(auto|en)\/([a-zA-Z-]+)/);
    if (match && match[2]) {
        setCurrentLangCode(match[2]);
        if (!lastSyncedLanguage.current) lastSyncedLanguage.current = match[2];
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = currentLangCode;
  }, [currentLangCode]);

  useEffect(() => {
    const root = document.documentElement;
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const darkColor = '#0f1521';
    const lightColor = '#e6dec6';

    if (isDark) {
        root.classList.add('dark');
        if (themeColorMeta) themeColorMeta.setAttribute('content', darkColor);
        if (statusBarMeta) statusBarMeta.setAttribute('content', 'black-translucent');
    } else {
        root.classList.remove('dark');
        if (themeColorMeta) themeColorMeta.setAttribute('content', lightColor);
        if (statusBarMeta) statusBarMeta.setAttribute('content', 'default');
    }
  }, [theme]);

  useEffect(() => {
    // If the language is NOT English, remove 'notranslate' to allow Google Translate to work.
    // If it IS English, keep 'notranslate' to prevent unwanted browser auto-translation.
    if (currentLangCode !== 'en') {
        document.documentElement.classList.remove('notranslate');
    } else {
        document.documentElement.classList.add('notranslate');
    }
  }, [currentLangCode]);

  const setGoogleTranslateCookie = (lang: string) => {
      const host = window.location.hostname;
      const domains = [host, `.${host}`];
      if (host.includes('.')) {
          const parts = host.split('.');
          if (parts.length > 2) {
              domains.push(`.${parts.slice(-2).join('.')}`);
          }
      }

      if (lang === 'en') {
          // Clear the cookie for all possible variations
          const past = "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
          document.cookie = `googtrans${past}`;
          domains.forEach(d => {
              document.cookie = `googtrans${past}; domain=${d}`;
          });
          return;
      }

      const cookieValue = `/en/${lang}`; // Explicit en source matches our init script
      const date = new Date();
      date.setTime(date.getTime() + (30*24*60*60*1000));
      const expires = "; expires=" + date.toUTCString();
      
      document.cookie = `googtrans=${cookieValue}; path=/;${expires}`;
      domains.forEach(d => {
          document.cookie = `googtrans=${cookieValue}; path=/; domain=${d};${expires}`;
      });
  };

  const handleLanguageSelect = (code: string) => {
      setGoogleTranslateCookie(code);
      localStorage.setItem('user_lang_preference', code);
      setCurrentLangCode(code);
      lastSyncedLanguage.current = code;
      
      if (user) {
          saveUserSettings(user.uid, { language: code }).catch(e => console.error("Language save error:", e?.message || String(e)));
      }
      
      // The most reliable cross-environment mechanism: Use hash and reload
      if (code !== 'en') {
          window.location.hash = `googtrans(en|${code})`;
      } else {
          window.location.hash = '';
      }
      window.location.reload();
  };

  useEffect(() => {
      const contentUnsubscribe = onPageContentUpdate((data) => {
          if (data) {
              setPageContent(prev => ({ ...prev, ...data }));
              setIsPageContentLoaded(true);
              if (data['app_logo']) {
                  updateDynamicFavicons(data['app_logo']);
              }
          } else {
              setIsPageContentLoaded(true);
          }
      });

      // Do not reset lastSyncedLanguage here so we can compare against the current UI language to avoid unnecessary reloads
      // lastSyncedLanguage.current = null;
      isSettingsInitialized.current = false;

      let settingsUnsubscribe = () => {};
      let bookmarksUnsubscribe = () => {};
      let catUnsub = () => {};
      let invoicesUnsubscribe = () => {};
      let paymentsUnsubscribe = () => {};
      let aiUsageUnsubscribe = () => {};

      if (user) {
          // Check for Stripe Checkout success
          const searchParams = new URLSearchParams(window.location.search);
          const sessionId = searchParams.get('session_id');
          if (sessionId) {
              // Clear it from URL immediately to prevent double firing on re-render
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.delete('session_id');
              window.history.replaceState({}, '', newUrl.toString());

              fetch('/api/verify-checkout', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sessionId, userId: user.uid })
              }).then(res => res.json()).then(async (data) => {
                  if (data.paid && data.amount_total > 0) {
                      // Security check: ensure the session belongs to this user
                      if (data.userId && data.userId !== user.uid) {
                          console.error("Payment session user mismatch");
                          return;
                      }

                      console.log(`Top-up verified: $${data.amount_total}`);
                      const currentSettings = await getUserSettings(user.uid);
                      // Add to topupBalance instead of changing the cap directly
                      const prevBalance = currentSettings?.topupBalance ?? topupBalance;
                      const newBalance = prevBalance + data.amount_total;
                      
                      console.log(`Updating balance: ${prevBalance} -> ${newBalance} for user ${user.uid}`);
                      await saveUserSettings(user.uid, { topupBalance: newBalance });
                      setTopupBalance(newBalance);
                      setPaymentNotice({ type: 'success', message: `Successfully topped up $${data.amount_total.toFixed(2)}!` });
                      setTimeout(() => setPaymentNotice(null), 5000);
                  } else {
                      setPaymentNotice({ type: 'error', message: "Payment verification failed or was cancelled." });
                      setTimeout(() => setPaymentNotice(null), 5000);
                  }
              }).catch(e => {
                  console.error("Top up verification failed:", e?.message || String(e));
                  setPaymentNotice({ type: 'error', message: "Could not verify payment. Please contact support if your balance does not update." });
                  setTimeout(() => setPaymentNotice(null), 8000);
              });
          }

          settingsUnsubscribe = onUserSettingsUpdate(user.uid, (settings) => {
              isSettingsInitialized.current = true;
              
              if (settings) {
                  if (settings.theme) setTheme(settings.theme);
                  if (settings.fontSize) setFontSize(settings.fontSize);
                  if (settings.monthlySpendCap !== undefined) setMonthlySpendCap(settings.monthlySpendCap);
                  if (settings.topupBalance !== undefined) setTopupBalance(settings.topupBalance);
                  if (settings.currentUsage !== undefined) setCurrentUsage(settings.currentUsage);
                  if (settings.payAsYouGo !== undefined) setPayAsYouGo(settings.payAsYouGo);
                  if (settings.quotaAlertThreshold !== undefined) setQuotaAlertThreshold(settings.quotaAlertThreshold);
                  
                   if (userRef.current && (settings.primaryPaymentMethod !== undefined || settings.hasPaymentMethod !== undefined || settings.notifications !== undefined)) {
                      const updatedUser = { 
                          ...userRef.current,
                          primaryPaymentMethod: settings.primaryPaymentMethod === undefined ? userRef.current.primaryPaymentMethod : settings.primaryPaymentMethod,
                          hasPaymentMethod: settings.hasPaymentMethod === undefined ? userRef.current.hasPaymentMethod : settings.hasPaymentMethod,
                          notifications: settings.notifications === undefined 
                            ? (userRef.current.notifications || DEFAULT_NOTIFICATIONS) 
                            : { ...DEFAULT_NOTIFICATIONS, ...settings.notifications }
                      };
                      const hasChanged = 
                          updatedUser.primaryPaymentMethod !== userRef.current.primaryPaymentMethod ||
                          updatedUser.hasPaymentMethod !== userRef.current.hasPaymentMethod ||
                          JSON.stringify(updatedUser.notifications) !== JSON.stringify(userRef.current.notifications);
                      if (hasChanged) {
                          setUser(updatedUser);
                      }
                  }

                  const newLang = settings.language || 'en';
                  const currentLang = lastSyncedLanguage.current || 'en';
                  
                  if (newLang !== currentLang && isSettingsInitialized.current) {
                      localStorage.setItem('user_lang_preference', newLang);
                      lastSyncedLanguage.current = newLang;
                      setCurrentLangCode(newLang);
                      
                      setGoogleTranslateCookie(newLang);
                      
                      // RELOAD SAFEGUARD: Use sessionStorage to strictly guarantee we do not enter an infinite 
                      // reload loop if cookies are being blocked or are failing to parse.
                      const sessionKey = 'langSyncReloaded_' + newLang;
                      if (!sessionStorage.getItem(sessionKey)) {
                        sessionStorage.setItem(sessionKey, 'true');
                        if (newLang !== 'en') {
                            window.location.hash = `googtrans(en|${newLang})`;
                        } else {
                            window.location.hash = '';
                        }
                        window.location.reload();
                      }
                  }
              }
          });

          bookmarksUnsubscribe = onBookmarksUpdate(user.uid, (data) => {
              setBookmarks(data);
          });

          catUnsub = onBookmarkCategoriesUpdate(user.uid, (data) => {
              setBookmarkCategories(data);
          });

          invoicesUnsubscribe = onInvoicesUpdate(user.uid, (data) => {
              setInvoices(data);
          });

          paymentsUnsubscribe = onPaymentsUpdate(user.uid, (data) => {
              setPayments(data);
          });

          aiUsageUnsubscribe = onAiUsageUpdate(user.uid, (data) => {
              if (data) {
                  setAiUsage({
                      dailyFreeUsed: data.dailyFreeUsed || 0,
                      freeLimit: data.freeLimit || 10,
                      credits: data.credits || 0
                  });
              }
          });
      }
      
      return () => {
          contentUnsubscribe();
          settingsUnsubscribe();
          bookmarksUnsubscribe();
          catUnsub();
          invoicesUnsubscribe();
          paymentsUnsubscribe();
          aiUsageUnsubscribe();
      };
  }, [user]);

  useEffect(() => {
    // Sync React state when Google Translate widget changes
    const checkCombo = () => {
        const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (combo && !combo.hasAttribute('data-app-listener')) {
            combo.setAttribute('data-app-listener', 'true');

            // Apply initial preference: Try to set the UI to match, but NEVER reload here to prevent loops.
            // Google Translate's native script will process the cookie.
            if (lastSyncedLanguage.current && combo.value !== lastSyncedLanguage.current) {
                combo.value = lastSyncedLanguage.current;
                combo.dispatchEvent(new Event('change'));
            }

            combo.addEventListener('change', () => {
                if (combo.value && combo.value !== lastSyncedLanguage.current) {
                    setCurrentLangCode(combo.value);
                    localStorage.setItem('user_lang_preference', combo.value);
                    if (user && isSettingsInitialized.current) {
                        lastSyncedLanguage.current = combo.value;
                        saveUserSettings(user.uid, { language: combo.value }).catch(e => console.error("Combo sync err:", e?.message || String(e)));
                    }
                }
            });
        }
    };

    const intervalId = setInterval(checkCombo, 1500);
    return () => clearInterval(intervalId);
  }, [user]);

  const handleThemeChange = (t: 'light' | 'dark' | 'system') => {
      if (t === theme) return;
      setTheme(t);
      if (user) saveUserSettings(user.uid, { theme: t });
  };

  const handleAddBookmarkCategory = async (name: string, parentId: string | null) => {
      if (!user) return;
      const id = Math.random().toString(36).substring(2, 11);
      const path = parentId 
          ? `${bookmarkCategories.find(c => c.id === parentId)?.path}/${name}`
          : name;
      
      // Check if path already exists
      if (bookmarkCategories.some(c => c.path === path)) return;

      await saveBookmarkCategory(user.uid, { id, name, parentId, path });
  };

  const handleUpdateBookmarkCategory = async (categoryId: string, newName: string) => {
      if (!user) return;
      const category = bookmarkCategories.find(c => c.id === categoryId);
      if (!category || category.name === newName) return;

      const oldPath = category.path;
      const newPath = category.parentId 
          ? `${bookmarkCategories.find(c => c.id === category.parentId)?.path}/${newName}`
          : newName;

      // Use a recursive function to update all sub-paths
      const updateCategoryRecursive = async (catId: string, currentOldPath: string, currentNewPath: string) => {
          const cat = bookmarkCategories.find(c => c.id === catId);
          if (!cat) return;

          // Update the category itself
          await saveBookmarkCategory(user.uid, { ...cat, path: currentNewPath, name: catId === categoryId ? newName : cat.name });

          // Update bookmarks in this category
          const bookmarksToUpdate = bookmarks.filter(b => b.categoryPath === currentOldPath);
          for (const bm of bookmarksToUpdate) {
              await saveBookmark(user.uid, { ...bm, categoryPath: currentNewPath });
          }

          // Update child categories
          const children = bookmarkCategories.filter(c => c.parentId === catId);
          for (const child of children) {
              const childNewPath = `${currentNewPath}/${child.name}`;
              await updateCategoryRecursive(child.id, child.path, childNewPath);
          }
      };

      await updateCategoryRecursive(categoryId, oldPath, newPath);
  };

  const handleDeleteBookmarkCategory = async (categoryId: string) => {
      if (!user) return;
      const category = bookmarkCategories.find(c => c.id === categoryId);
      if (!category) return;

      const pathToDelete = category.path;

      // Recursive deletion helper
      const deleteRecursive = async (catId: string, path: string) => {
          // Find direct subcategories
          const subCats = bookmarkCategories.filter(c => c.parentId === catId);
          for (const sub of subCats) {
              await deleteRecursive(sub.id, sub.path);
          }

          // Move bookmarks in this path to 'General' to prevent data loss
          const orphanedBookmarks = bookmarks.filter(b => b.categoryPath === path);
          for (const bm of orphanedBookmarks) {
              await saveBookmark(user.uid, { ...bm, categoryPath: 'General' });
          }

          // Finally delete the category document
          await deleteBookmarkCategory(user.uid, catId);
      };

      await deleteRecursive(categoryId, pathToDelete);
  };

  const handleSaveBookmark = async (bookmark: Omit<Bookmark, 'id' | 'timestamp'>) => {
      if (!user) return;
      const id = bookmarkDialogState.existingBookmark?.id || Math.random().toString(36).substring(2, 11);
      await saveBookmark(user.uid, { ...bookmark, id, timestamp: Date.now() });
  };

  const handleDeleteBookmark = async (bookmarkId: string) => {
      if (!user) return;
      await deleteBookmark(user.uid, bookmarkId);
  };

  const handleFontSizeChange = (s: 'sm' | 'base' | 'lg') => {
      if (s === fontSize) return;
      setFontSize(s);
      if (user) saveUserSettings(user.uid, { fontSize: s });
  };

  const handleSpendCapChange = (cap: number) => {
      setMonthlySpendCap(cap);
      if (user) saveUserSettings(user.uid, { monthlySpendCap: cap });
  };

  const handlePayAsYouGoChange = (payAsYouGo: boolean) => {
      setPayAsYouGo(payAsYouGo);
      if (user) saveUserSettings(user.uid, { payAsYouGo });
  };

  const handleQuotaAlertThresholdChange = (threshold: number) => {
      setQuotaAlertThreshold(threshold);
      if (user) saveUserSettings(user.uid, { quotaAlertThreshold: threshold });
  };

  const handleTopup = useCallback(async (amount: number, method?: string) => {
      if (!user) {
          alert("Please log in to top up your account.");
          return;
      }
      
      const activeMethod = method || user.primaryPaymentMethod || 'stripe';

      if (activeMethod === 'google-pay') {
          try {
              setTopupBalance(prev => prev + amount);
              setPaymentNotice({ type: 'success', message: `Successfully added $${amount} AI Quota via Google Pay!` });
              
              if (user.uid) {
                  try {
                      await saveUserSettings(user.uid, { topupBalance: (topupBalance || 0) + amount });
                  } catch (dbErr) {
                      console.warn("Could not sync Google Pay topup to Firestore:", dbErr);
                  }
              }
              setTimeout(() => setPaymentNotice(null), 5000);
              return;
          } catch (err: any) {
              console.error("Google Pay topup processing error:", err);
          }
      }

      try {
          setPaymentNotice({ type: 'success', message: "Redirecting to secure Stripe Checkout..." });
          
          const successUrl = `${window.location.origin}/`;
          const cancelUrl = `${window.location.origin}/`;
          
          const res = await fetch('/api/create-checkout/topup', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  amount,
                  successUrl,
                  cancelUrl,
                  userId: user.uid,
                  paymentMethod: activeMethod
              })
          });
          
          if (!res.ok) {
              const err = await res.json();
              throw new Error(err.error || "Failed to initiate Stripe checkout.");
          }
          
          const data = await res.json();
          if (data.url) {
              window.location.href = data.url;
          } else {
              throw new Error("No checkout URL returned from server.");
          }
      } catch (e: any) {
          console.error("Top-up redirection failed:", e);
          setPaymentNotice({ type: 'error', message: e?.message || "Could not launch Stripe Checkout. Please make sure your STRIPE_SECRET_KEY is configured." });
          setTimeout(() => setPaymentNotice(null), 5000);
      }
  }, [user]);

  const handlePayInvoice = useCallback(async (invoiceId: string, amount: number) => {
    if (!user) return;
    
    // Create a payment record
    const paymentRecord: PaymentRecord = {
        id: `PAY-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        amount: amount,
        type: 'bill-payment',
        method: user.primaryPaymentMethod || 'stripe',
        status: 'success'
    };
    
    await Promise.all([
        updateInvoiceStatus(user.uid, invoiceId, 'paid'),
        savePaymentRecord(user.uid, paymentRecord)
    ]);

    // Send confirmation email and report to info@edgarcayce.ch
    try {
        await fetch('/api/billing/send-bill', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: user.email,
                amount: amount.toFixed(2),
                period: `Invoice ${invoiceId}`
            })
        });

        await fetch('/api/billing/report-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: user.email,
                amount: amount,
                method: user.primaryPaymentMethod || 'stripe',
                type: `invoice-payment (Invoice ${invoiceId})`
            })
        });
    } catch (e) {
        console.error("Failed to send payment confirmation email or report:", e);
    }
  }, [user]);

  // One-time reset logic as requested by user to start from zero for production
  useEffect(() => {
    if (user && !localStorage.getItem(`reset_usage_${user.uid}`)) {
        const performReset = async () => {
            console.log("Performing one-time usage reset...");
            setCurrentUsage(0);
            setAiUsage({ dailyFreeUsed: 0, freeLimit: 10, credits: 0 });
            setTopupBalance(0);
            
            // Clear local daily tracker for ChatPage
            localStorage.removeItem('dailyMessagesUsed');
            localStorage.removeItem('lastUsageDate');
            
            // Persist to Firestore
            await Promise.all([
                saveUserSettings(user.uid, { currentUsage: 0, topupBalance: 0 }),
                saveAiUsage(user.uid, { dailyFreeUsed: 0, credits: 0 })
            ]);
            
            localStorage.setItem(`reset_usage_${user.uid}`, 'true');
        };
        performReset();
    }
  }, [user]);

  const handleIncrementUsage = async (amount: number) => {
      const newUsage = currentUsage + amount;
      setCurrentUsage(newUsage);
      if (user) {
          saveUserSettings(user.uid, { currentUsage: newUsage });
          
          // Pay as you go logic: auto-increase cap to allow continuous usage
          const totalAllowance = monthlySpendCap + topupBalance;
          if (payAsYouGo && newUsage >= totalAllowance) {
              const adjustedCap = Math.ceil((newUsage + 5) / 5) * 5 - topupBalance; // Round up to next $5 block relative to total
              const finalCap = Math.max(monthlySpendCap, adjustedCap);
              setMonthlySpendCap(finalCap);
              saveUserSettings(user.uid, { monthlySpendCap: finalCap });
              // In production, initiate an invoice/charge here
          }

          // Threshold check (now currency based)
          if (user.email && quotaAlertThreshold > 0) {
              if (newUsage >= quotaAlertThreshold) {
                  fetch('/api/send-quota-alert', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: user.email, used: newUsage, cap: monthlySpendCap + topupBalance })
                  }).catch(e => console.error("Quota alert err:", e?.message || String(e)));
              }
          }
      }
  };

  const [bookmarkDialogState, setBookmarkDialogState] = useState<{
      isOpen: boolean;
      initialTitle: string;
      fileId: string;
      fileName: string;
      paragraphId?: string;
      paragraphText?: string;
      existingBookmark?: Bookmark;
  }>({
      isOpen: false,
      initialTitle: '',
      fileId: '',
      fileName: '',
      paragraphId: undefined,
      paragraphText: undefined,
      existingBookmark: undefined
  });

  const openBookmarkDialog = (title: string, fileId: string, fileName: string, paragraphId?: string, paragraphText?: string) => {
      const existing = bookmarks.find(b => b.fileId === fileId && b.paragraphId === paragraphId);
      
      // If user clicks an active bookmark, and they are NOT opening it to edit (simple toggle request)
      if (existing && !paragraphText && activePage !== 'profile') {
          handleDeleteBookmark(existing.id);
          return;
      }

      setBookmarkDialogState({
          isOpen: true,
          initialTitle: title,
          fileId,
          fileName,
          paragraphId,
          paragraphText,
          existingBookmark: existing
      });
  };

  useEffect(() => {
    const id = 'google_translate_element'; 
    const init = () => {
        const el = document.getElementById(id);
        if (el && el.innerHTML === "" && (window as any).google?.translate?.TranslateElement) {
            new (window as any).google.translate.TranslateElement({
                pageLanguage: 'en',
                layout: (window as any).google.translate.TranslateElement.InlineLayout.HORIZONTAL,
                autoDisplay: false
            }, id);
        }
    };
    if ((window as any).googleTranslateLoaded) setTimeout(init, 50);
    window.addEventListener('google-translate-ready', init);
    const interval = setInterval(() => {
        if ((window as any).google?.translate?.TranslateElement) {
            init();
            clearInterval(interval);
        }
    }, 1000);
    return () => {
        window.removeEventListener('google-translate-ready', init);
        clearInterval(interval);
    };
  }, []);

  useEffect(() => {
      const widget = document.getElementById('google_translate_element');
      if (activePage === 'profile' || activePage === 'settings') return;
      if (widget) {
          if (user) {
              if (widget.parentElement !== document.body) document.body.appendChild(widget);
              widget.style.display = 'none';
          } else {
              if (widget.parentElement !== document.body) document.body.appendChild(widget);
              widget.style.display = 'none';
          }
      }
  }, [activePage, user]);

  const fetchReadingsFromBucket = useCallback(async () => {
    setIsLoadingBucket(true);
    const files: ArchiveFile[] = [];
    let marker = '';
    // Try custom domain first, fallback to storage.googleapis.com
    const baseUrl = `https://${READINGS_BUCKET}/`;
    const fallbackUrl = `https://storage.googleapis.com/${READINGS_BUCKET}/`;

    try {
      do {
        const baseOrigin = (window.location.origin && window.location.origin !== 'null') 
            ? window.location.origin 
            : (window.location.protocol + '//' + window.location.host);
        let currentUrl = new URL('/api/proxy-bucket', baseOrigin);
        currentUrl.searchParams.set('bucket', READINGS_BUCKET);
        if (marker) currentUrl.searchParams.set('marker', marker);
        
        console.log(`App: Fetching bucket via proxy ${currentUrl.toString()}`);
        let response = await fetch(currentUrl.toString());
        
        if (!response.ok) {
            console.warn(`App: Bucket fetch response not OK: ${response.status} ${response.statusText} for ${currentUrl.toString()}`);
            break;
        }
        
        const text = await response.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'text/xml');
        const contents = xml.querySelectorAll('Contents');
        contents.forEach(content => {
          const key = content.querySelector('Key')?.textContent || '';
          if (key.endsWith('.html') && !key.startsWith('_') && !key.includes('/.')) {
            const size = content.querySelector('Size')?.textContent || '0';
            const lastMod = content.querySelector('LastModified')?.textContent || '';
            let name = key.replace('.html', '').replace(/_/g, ' ');
            let category = 'General Archive';
            let summary = 'Uncategorized';
            const readingMatch = key.match(/Reading\s+(\d+)-(\d+)/i);
            if (readingMatch) {
                const num = parseInt(readingMatch[1]);
                const range1000Start = Math.floor((num - 1) / 1000) * 1000 + 1;
                const range1000End = range1000Start + 999;
                category = `Cases ${range1000Start}-${range1000End}`;
                const range100Start = Math.floor((num - 1) / 100) * 100 + 1;
                const range100End = range100Start + 99;
                summary = `Readings ${range100Start}-${range100End}`;
            } else {
                if (key.includes('/')) {
                    const parts = key.split('/');
                    category = parts[0]; 
                    if (parts.length > 2) summary = parts[1]; 
                    name = parts[parts.length - 1].replace('.html', '').replace(/_/g, ' ');
                }
            }
            files.push({
              id: key,
              name: name,
              type: 'html',
              path: key,
              size: (parseInt(size) / 1024).toFixed(1) + ' KB',
              lastModified: new Date(lastMod).toLocaleDateString(),
              category: category,
              summary: summary, 
              description: `Archival record: ${name}`
            });
          }
        });
        const nextMarker = xml.querySelector('NextMarker')?.textContent;
        marker = nextMarker || '';
      } while (marker);
      
      if (files.length > 0) {
        setBucketFiles(files.filter(f => f.category !== 'General Archive'));
      } else {
        console.warn("Proxy listing returned no files. Falling back to local default readings.");
        setBucketFiles(getFallbackReadings());
      }
    } catch (e: any) {
      const msg = e.message || String(e);
      console.error("Bucket fetch failed", msg);
      if (msg.includes('Failed to fetch')) {
        console.warn("Bucket fetch likely failed due to network restriction or server unavailability.");
      }
      console.warn("Falling back to local default readings after fetch error.");
      setBucketFiles(getFallbackReadings());
    } finally {
      setIsLoadingBucket(false);
    }
  }, []);

  const handleStartIndexing = useCallback(async () => {
    // Readings are indexed server-side by matrix.allin1.tech worker
  }, []);

  useEffect(() => {
    fetchReadingsFromBucket();
  }, [fetchReadingsFromBucket]);

  const wikiPages = useMemo(() => {
      const merged = new Map<string, ArchiveFile>();
      const currentMocks = getMockFiles(); 
      currentMocks.forEach(f => merged.set(f.id, f));
      firestoreArticles.forEach(f => {
          merged.set(f.id, f);
      });
      return Array.from(merged.values())
          .filter(f => !deletedFileIds.has(f.id))
          .sort((a, b) => a.name.localeCompare(b.name));
  }, [firestoreArticles, mockVersion, deletedFileIds]); 

  const wikiSearchResults = useMemo(() => {
      if (!wikiSearchTerm.trim()) return null;
      const lower = wikiSearchTerm.toLowerCase();
      return wikiPages
          .filter(p => p.name.toLowerCase().includes(lower) || p.description?.toLowerCase().includes(lower) || p.category?.toLowerCase().includes(lower))
          .map(p => ({
              file: p,
              relevanceScore: 100, 
              snippet: p.description || "No description available.",
              displayUrl: `wiki/${p.id}`,
              aiInsights: "Wiki Match"
          } as SearchResult));
  }, [wikiPages, wikiSearchTerm]);

  useEffect(() => {
      if (selectedFile && firestoreArticles.length > 0) {
          const updatedVersion = firestoreArticles.find(f => f.id === selectedFile.id);
          if (updatedVersion && updatedVersion !== selectedFile) {
              if (updatedVersion.lastModified !== selectedFile.lastModified || updatedVersion.author !== selectedFile.author) {
                  setSelectedFile(updatedVersion);
              }
          }
      }
  }, [firestoreArticles, selectedFile]);

  const isAdmin = user?.email === ADMIN_EMAIL;

  const handleEmailLogin = useCallback(async (email: string, password: string) => {
    try {
        const result = await signInWithEmail(email, password);
        if (result && result.user) {
            localStorage.setItem('lastLoginProvider', 'email');
            setUser({ 
               uid: result.user.uid, 
               name: result.user.displayName || 'User', 
               email: result.user.email || '', 
               picture: result.user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${result.user.uid}` 
            });
        }
    } catch (e: any) {
        throw e;
    }
  }, []);

  const checkStoredCredentials = useCallback(async () => {
    if (userRef.current || !window.navigator.credentials || !authReady) return;
    
    // Check if we've already prompted in this component session to avoid loops
    if ((window as any)._authPrompted) return;
    (window as any)._authPrompted = true;

    try {
      const lastProvider = localStorage.getItem('lastLoginProvider');
      
      // Small delay to ensure browser has populated credentials
      await new Promise(r => setTimeout(r, 1000));
      
      // Re-verify user hasn't logged in during the timeout
      if (userRef.current) return;
      
      if (lastProvider === 'google') {
          console.log("Last session was Google, opening auth modal to prevent auto-popup block");
          setIsAuthModalOpen(true);
          return;
      }
      
      const cred = await (window.navigator.credentials as any).get({
        password: true,
        federated: {
          providers: ['https://accounts.google.com']
        },
        mediation: 'optional'
      });
      
      if (cred && !userRef.current) {
        if ((cred as any).type === 'federated') {
          console.log("Found federated credential, opening auth modal to prevent auto-popup block");
          setIsAuthModalOpen(true);
        } else if ((cred as any).type === 'password') {
          const passwordCred = cred as any;
          if (passwordCred.id && passwordCred.password) {
             console.log("Found saved password, attempting auto-login");
             try {
                await handleEmailLogin(passwordCred.id, passwordCred.password);
             } catch (e) {
                console.warn("Auto-login with saved password failed:", e);
                setIsAuthModalOpen(true); // Open modal if auto-login fails
             }
          }
        }
      }
    } catch (e) {
      console.debug("Credential check failed or silent login not possible:", e);
    }
  }, [authReady, handleEmailLogin]);

  useEffect(() => {
    if (authReady && !redirectChecked.current) {
        redirectChecked.current = true;
        checkRedirectResult().then(result => {
            if (result && result.user) {
                // Success
            } else {
                // Only check credentials if no redirect result
                checkStoredCredentials();
            }
        }).catch(err => {
            const msg = err.message || String(err);
            console.error("Redirect check failed:", msg);
            
            if (err.code === 'auth/network-request-failed' || msg.includes('network-request-failed')) {
                console.warn("Firebase redirect check failed due to network-request-failed error (common in sandboxed or iframe environments). Safely falling back to local credentials/guest mode.");
                checkStoredCredentials();
            } else if (err.code === 'auth/unauthorized-domain' || msg.includes('unauthorized-domain')) {
                setAuthError({
                    message: "This domain is not authorized in your Firebase project.",
                    domain: window.location.hostname
                });
            } else {
                console.error("Redirect check fallback:", msg);
                checkStoredCredentials();
            }
        });
    }

    const unsubscribeAuth = onAuthStateChange((firebaseUser) => {
      setAuthReady(true);
      if (firebaseUser) {
        setUser({ 
            uid: firebaseUser.uid, 
            name: firebaseUser.displayName || 'User', 
            email: firebaseUser.email || '', 
            picture: firebaseUser.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${firebaseUser.uid}`,
            notifications: DEFAULT_NOTIFICATIONS
        });
        saveAppRegistration(firebaseUser.uid, APP_ID).catch(console.error);
        onThreadsUpdate(firebaseUser.uid, setThreads);
      } else {
        setUser(null);
        setIsEditorMode(false);
        setThreads([]);
      }
    });

    const unsubscribeArticles = subscribeToWikiArticles((articles) => {
        setFirestoreArticles(articles);
        setIsWikiLoaded(true); 
    });
    
    const wikiTimeout = setTimeout(() => setIsWikiLoaded(true), 5000);

    return () => { 
        unsubscribeAuth(); 
        unsubscribeArticles(); 
        clearTimeout(wikiTimeout);
    };
  }, []);

  const handleSignIn = () => { 
    setAuthError(null);
    setIsAuthModalOpen(true);
  };
  
  const handleSignOut = async () => { 
      localStorage.removeItem('lastLoginProvider');
      await logout(); 
      setUser(null); 
      setIsEditorMode(false);
      navigateInternal('/'); 
  };

  const handleSaveEntry = useCallback(async (
    id: string, 
    data: { title: string; content: string; category: string; imageUrl?: string; startYear?: number; endYear?: number }, 
    isAutoSave: boolean = false
  ): Promise<boolean> => {
    if (!user) return false;
    const allCurrentFiles = [...getMockFiles(), ...firestoreArticles];
    const existingFile = allCurrentFiles.find(f => f.id === id);
    const finalImageUrl = data.imageUrl !== undefined ? data.imageUrl : (existingFile?.imageUrl || ''); 

    const fileData = {
        ...existingFile, 
        id: id, 
        name: data.title, 
        type: 'html' as 'html', 
        category: data.category, 
        lastModified: new Date().toLocaleDateString(),
        imageUrl: finalImageUrl, 
        author: user.email, 
        status: (isAdmin ? 'published' : 'pending') as 'published' | 'pending', 
        content: data.content, 
        authorId: user.uid,
        startYear: data.startYear,
        endYear: data.endYear
    };

    const success = await saveWikiArticle(id, fileData);
    if (selectedFile && selectedFile.id === id) {
        setSelectedFile(prev => prev ? ({ ...prev, ...fileData }) : null);
    }
    
    if (success && !isAutoSave && !selectedFile) {
        navigateInternal('/wiki');
        setDeletedFileIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }
    return success;
  }, [user, isAdmin, firestoreArticles, selectedFile]);

  const handleDeleteWikiPage = useCallback(async (fileId: string) => {
      setDeletedFileIds(prev => new Set(prev).add(fileId));
      if (selectedFile?.id === fileId) {
          navigateInternal('/wiki');
      }
      deleteMockFile(fileId); 
      try {
          await deleteWikiArticle(fileId);
      } catch (e: any) {
          console.error("Delete exception", e.message || e);
      }
  }, [selectedFile]);

  const handleSavePageContent = useCallback((key: string, content: string) => {
    setPageContent(prev => ({ ...prev, [key]: content }));
    updatePageContent(key, content);
  }, []);

  const handleLogoAssetSelect = (assetStoragePath: string) => {
      setPageContent(prev => ({ ...prev, 'app_logo': assetStoragePath }));
      updateDynamicFavicons(getResizedImageUrl(assetStoragePath, '300x300'));
      updatePageContent('app_logo', assetStoragePath);
      setIsLogoModalOpen(false);
  };

  const handleLogoUrlApply = () => {
      if (!logoMediaUrlInput.trim()) return;
      const url = logoMediaUrlInput.trim();
      setPageContent(prev => ({ ...prev, 'app_logo': url }));
      updateDynamicFavicons(getResizedImageUrl(url, '300x300'));
      updatePageContent('app_logo', url);
      setIsLogoModalOpen(false);
  };

  const handleLogoFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoUploading(true);
    try {
        const base64 = await fileToBase64(file);
        setPageContent(prev => ({ ...prev, 'app_logo': base64 }));
        const path = `${FIREBASE_STORAGE_BASE_PATH}app_branding/logo_${Date.now()}_${file.name}`;
        const storagePath = await uploadMedia(file, path);
        updateDynamicFavicons(getResizedImageUrl(storagePath, '300x300'));
        setPageContent(prev => ({ ...prev, 'app_logo': storagePath }));
        updatePageContent('app_logo', storagePath);
        setIsLogoModalOpen(false);
    } catch (err: any) {
        console.error("Logo processing failed", err.message || err);
    } finally {
        setLogoUploading(false);
    }
  };
  
  const appLogo = pageContent['app_logo'];
  useEffect(() => {
      if (appLogo) {
          updateDynamicFavicons(getResizedImageUrl(appLogo, '300x300'));
      }
  }, [appLogo]);

  const handleNavigate = useCallback((action: () => void) => {
      if (isEditorDirty) {
          setPendingNavigation({ run: action });
          setShowUnsavedDialog(true);
      } else {
          action();
      }
  }, [isEditorDirty]);

  const handleConfirmDiscard = () => {
      setIsEditorDirty(false); 
      setShowUnsavedDialog(false);
      if (pendingNavigation) pendingNavigation.run();
      setPendingNavigation(null);
  };

  const handleConfirmSave = async () => {
      if (editorRef.current) {
          const success = await editorRef.current.save();
          if (success) {
              setIsEditorDirty(false);
              setShowUnsavedDialog(false);
              if (pendingNavigation) pendingNavigation.run();
              setPendingNavigation(null);
          } else {
              alert("Save failed. Please check your connection.");
          }
      } else {
          handleConfirmDiscard();
      }
  };

  const findFileByIdentifier = useCallback((identifier: string): ArchiveFile | null => {
        if (!identifier) return null;
        const all = [...bucketFiles, ...wikiPages];
        
        const norm = (s: string) => (s || '').toLowerCase().replace(/^(readings\/|wiki\/)/, '').replace(/\.(txt|html|json|md)$/, '');
        const targetNorm = norm(identifier);

        const byId = all.find(f => f.id === identifier || norm(f.id) === targetNorm);
        if (byId) return byId;

        const byName = all.find(f => f.name === identifier || norm(f.name) === targetNorm);
        if (byName) return byName;

        const byPath = all.find(f => f.path === identifier || norm(f.path) === targetNorm || f.path.endsWith('/' + identifier) || identifier.endsWith('/' + f.name));
        if (byPath) return byPath;
        
        const getReadingNumbers = (id: string): { n1: number; n2: number } | null => {
            if (!id) return null;
            const matches = id.match(/(\d+)[-_](\d+)/);
            if (matches && matches.length === 3) {
                return { n1: parseInt(matches[1], 10), n2: parseInt(matches[2], 10) };
            }
            return null;
        };
        const paramParts = getReadingNumbers(identifier);
        if (paramParts) {
            const byReadingNum = all.find(f => {
                const fileParts = getReadingNumbers(f.name) || getReadingNumbers(f.id) || getReadingNumbers(f.path);
                return fileParts && fileParts.n1 === paramParts.n1 && fileParts.n2 === paramParts.n2;
            });
            if (byReadingNum) return byReadingNum;
        }

        return null;
    }, [bucketFiles, wikiPages]);

    const applyUrlState = useCallback((location: Location) => {
        const { pathname, search, hash } = location;
        const params = new URLSearchParams(search);
        setUrlSearchParams(params);
        setCurrentPathname(pathname || '/');
        setCurrentHash(hash || '');

        const [_, pageStr, id] = pathname.split('/');
        const page = (pageStr || 'legacy') as Page;
    
        // Set the main page first
        setActivePage(page);
    
        // Reset things that are always reset
        setUrlError(null);
    
        // Handle states that are mutually exclusive (chat vs. file view)
        const fileParam = params.get('file');
        
        if (page === 'chat') {
            setSelectedFile(null);
            setReadingHighlight('');
            setActiveThreadId(id);
        } else if (page === 'readings' || page === 'wiki' || ((page === 'profile' || page === 'settings') && fileParam)) {
            setActiveThreadId(undefined);
            const fileId = fileParam || (id ? decodeURIComponent(id) : null);
            
            if (fileId) {
                const file = findFileByIdentifier(fileId);
                if (file) {
                    setSelectedFile(file);
                    setReadingHighlight(params.get('highlight') || '');
                } else {
                    setSelectedFile(null);
                    setUrlError(fileId);
                }
            } else {
                setSelectedFile(null);
            }
        } else {
            // For other pages, clear both detail views
            setActiveThreadId(undefined);
            setSelectedFile(null);
            setReadingHighlight('');
        }
    
        // Handle other states
        if (page === 'community' && id === 'contact') {
            setForceShowContact(true);
            setContactSubject(params.get('subject') || 'General Inquiry');
        } else {
            setForceShowContact(false);
        }
    
        setActiveCategory(params.get('category') || 'all');
        const query = params.get('q') || '';
        setReadingsQuery(page === 'readings' ? query : '');
        setWikiSearchTerm(page === 'wiki' ? query : '');
    }, [findFileByIdentifier, isStudio]);

  const navigateWithoutHistory = useCallback((path: string) => {
    const baseOrigin = (window.location.origin && window.location.origin !== 'null') 
        ? window.location.origin 
        : (window.location.protocol + '//' + window.location.host);
    const url = new URL(path, baseOrigin);
    const mockLocation = {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        state: null,
    } as unknown as Location;
    applyUrlState(mockLocation);
  }, [applyUrlState]);

  const navigateWithHistory = useCallback((path: string, options?: { replace?: boolean }) => {
    const currentPath = window.location.pathname + window.location.search;
    if (path !== currentPath) {
        if (options?.replace) {
            window.history.replaceState(null, '', path);
        } else {
            window.history.pushState(null, '', path);
        }
    }
    applyUrlState(window.location);
  }, [applyUrlState]);

  const navigateInternal = useCallback((path: string, options?: { replace?: boolean, hash?: string }) => {
    let finalPath = path;
    if (options?.hash) {
        finalPath = `${path}#${options.hash}`;
    }
    if (isStudio) {
        navigateWithoutHistory(finalPath);
    } else {
        navigateWithHistory(finalPath, options);
    }
  }, [isStudio, navigateWithHistory, navigateWithoutHistory]);

  const safeSetActivePage = useCallback((page: Page) => {
      handleNavigate(() => {
          navigateInternal(`/${page === 'legacy' ? '' : page}`);
      });
  }, [handleNavigate, navigateInternal]);
  
  const handleCategorySelect = useCallback((category: string) => {
      handleNavigate(() => {
          const search = new URLSearchParams(urlSearchParams.toString());
          if (category !== 'all' && category !== 'all-collections' && category !== 'my-wiki') {
            search.set('category', category);
            if (activePage === 'profile' || activePage === 'settings') search.delete('collection');
          } else {
            search.delete('category');
            if ((activePage === 'profile' || activePage === 'settings') && category === 'all-collections') {
                search.set('collection', 'all');
            } else if ((activePage === 'profile' || activePage === 'settings') && category === 'my-wiki') {
                search.set('collection', 'wiki');
            } else if (activePage === 'profile' || activePage === 'settings') {
                search.delete('collection');
            }
          }
          
          if (activePage === 'profile' || activePage === 'settings') {
              navigateInternal(`/profile${search.toString() ? '?' + search.toString() : ''}`);
          } else {
              const page = activePage === 'readings' ? 'readings' : 'wiki';
              navigateInternal(`/${page}${search.toString() ? '?' + search.toString() : ''}`);
          }
      });
  }, [handleNavigate, activePage, navigateInternal]);

  const handleReadingsSearch = useCallback((query: string) => {
      handleNavigate(() => {
          const search = new URLSearchParams();
          if (query) search.set('q', query);
          navigateInternal(`/readings${search.toString() ? '?' + search.toString() : ''}`);
      });
  }, [handleNavigate, navigateInternal]);

  const handleChatStart = useCallback((query: string) => {
      handleNavigate(() => {
          setChatInitialQuery(query);
          navigateInternal('/chat');
      });
  }, [handleNavigate, navigateInternal]);

  const onNavigateToContact = useCallback((subject?: string) => {
      handleNavigate(() => {
          const search = new URLSearchParams();
          if (subject) search.set('subject', subject);
          navigateInternal(`/community/contact${search.toString() ? '?' + search.toString() : ''}`);
      });
  }, [handleNavigate, navigateInternal]);

  const handleFileSelect = useCallback((file: ArchiveFile, highlight?: string) => {
    handleNavigate(() => {
        const isReading = file.name.startsWith('Reading ') || (file.path && file.path.startsWith('Reading '));
        const page = isReading ? 'readings' : 'wiki';
        const search = new URLSearchParams(urlSearchParams.toString());
        if (highlight) search.set('highlight', highlight);
        
        // Preserve current search and category context in the reader URL
        const currentQuery = isReading ? readingsQuery : wikiSearchTerm;
        if (currentQuery) search.set('q', currentQuery);
        if (activeCategory && activeCategory !== 'all') search.set('category', activeCategory);

        if (activePage === 'profile') {
            search.set('file', file.id);
            navigateInternal(`/profile?${search.toString()}`);
        } else {
            navigateInternal(`/${page}/${encodeURIComponent(file.id)}${search.toString() ? '?' + search.toString() : ''}`);
        }
    });
  }, [handleNavigate, navigateInternal, readingsQuery, wikiSearchTerm, activeCategory, activePage]);

  const handleNavigateToBookmark = useCallback((bookmark: Bookmark) => {
      const file = [...bucketFiles, ...firestoreArticles].find(f => f.id === bookmark.fileId);
      if (file) {
          handleFileSelect(file, bookmark.paragraphText || undefined);
      }
  }, [bucketFiles, firestoreArticles, handleFileSelect]);

  const handleGlobalMockUpdate = async () => {
    if (activePage === 'legacy' && !selectedFile) {
        setPageContent(prev => ({
            ...prev,
            'legacy_hero_image': `https://images.unsplash.com/photo-1507842217343-583bb7270b66?q=80&w=2000&auto=format&fit=crop`, // Library/Hero
            'legacy_research_image': `https://images.unsplash.com/photo-1532012197267-da84d127e765?q=80&w=1000&auto=format&fit=crop`, // Books/Research
            'app_logo': `${FIREBASE_STORAGE_BASE_PATH}app_branding/logo.webp`
        }));
        
        alert("Assets Optimized! \n\nImage references have been updated to target optimized root-relative assets for the live production environment.");

    } else if (selectedFile && editorRef.current) {
        editorRef.current.updateMocks();
    } else {
        alert("No editable context available.");
    }
  };

  const handleThreadSelect = useCallback((t: DiscussionThread) => {
    handleNavigate(() => {
      navigateInternal(`/chat/${t.id}`);
    });
  }, [handleNavigate, navigateInternal]);

  const handleThreadDelete = useCallback((id: string) => {
    if (user) {
      deleteThreadFromFirestore(user.uid, id);
      setThreads(prev => prev.filter(t => t.id !== id));
      if (activeThreadId === id) {
          navigateInternal('/chat');
      }
    }
  }, [user, activeThreadId, navigateInternal]);

  const handleNewChat = useCallback(() => {
    handleNavigate(() => {
      navigateInternal('/chat');
    });
  }, [handleNavigate, navigateInternal]);

  const handleThreadTitleUpdate = useCallback((id: string, title: string) => {
    const t = threads.find(x => x.id === id);
    if (t && user) {
      saveThreadToFirestore(user.uid, { ...t, title });
    }
  }, [threads, user]);

  const handleSidebarToggleExpand = useCallback((id: string) => {
      setSidebarExpandedItems(prevItems => {
          const newSet = new Set(prevItems);
          if (newSet.has(id)) {
              newSet.delete(id);
          } else {
              newSet.add(id);
          }
          return newSet;
      });
  }, []);

  const handleCommunityCreateGroup = useCallback(() => alert("Group creation coming soon"), []);
  const handleCommunityDiscoverGroups = useCallback(() => alert("Group discovery coming soon"), []);
  
    useEffect(() => {
        if (isLoadingBucket || !isWikiLoaded) return;
        
        if (isStudio) {
            const rootLocation = { pathname: '/', search: '' } as unknown as Location;
            applyUrlState(rootLocation);
        } else {
            applyUrlState(window.location);
            const handlePopState = () => applyUrlState(window.location);
            window.addEventListener('popstate', handlePopState);
            return () => window.removeEventListener('popstate', handlePopState);
        }
    }, [isLoadingBucket, isWikiLoaded, applyUrlState, isStudio]);


  const isContactView = activePage === 'community' && (!user || forceShowContact);
  const showSidebar = activePage !== 'legacy' && !isContactView;

  const sidebarElement = useMemo(() => (
      <div className={`z-[60] h-full border-r border-stone-200 dark:border-stone-800 transition-all duration-300 ease-in-out absolute md:relative top-0 bottom-0 left-0 w-80 shrink-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:-ml-80 md:translate-x-0'}`}>
          <Sidebar 
                activePage={(activePage === 'settings' ? 'profile' : activePage) as 'wiki' | 'chat' | 'profile' | 'community' | 'readings'}
                onCategorySelect={handleCategorySelect} 
                activeCategory={activeCategory}
                threads={threads}
                activeThreadId={activeThreadId}
                onThreadSelect={handleThreadSelect}
                onThreadDelete={handleThreadDelete}
                onThreadTitleUpdate={handleThreadTitleUpdate}
                isAuthenticated={!!user}
                allFiles={(activePage === 'readings') ? bucketFiles : ((activePage === 'profile' || activePage === 'settings') ? [...bucketFiles, ...wikiPages] : wikiPages)}
                onFileSelect={handleFileSelect}
                onFileDelete={handleDeleteWikiPage}
                selectedFileId={selectedFile?.id}
                user={user}
                expandedItems={sidebarExpandedItems}
                onToggleExpand={handleSidebarToggleExpand}
                sidebarSearch={sidebarSearchTerm}
                onSearchChange={setSidebarSearchTerm}
                communityGroups={groups}
                communityFilter={communityFilter}
                communitySelectedGroupId={selectedGroupId}
                communityPosts={communityPosts}
                onCommunityFilterChange={setCommunityFilter}
                onCommunityGroupSelect={setSelectedGroupId}
                onCommunityCreateGroup={handleCommunityCreateGroup}
                onCommunityDiscoverGroups={handleCommunityDiscoverGroups}
                onNewChat={handleNewChat}
                bookmarks={bookmarks}
                bookmarkCategories={bookmarkCategories}
                onOpenBookmarkDialog={openBookmarkDialog}
                onUpdateCategory={handleUpdateBookmarkCategory}
                onDeleteCategory={handleDeleteBookmarkCategory}
                onNavigate={navigateInternal}
            />
      </div>
  ), [
      activePage, activeCategory, threads, activeThreadId, user, bucketFiles, wikiPages, selectedFile, isSidebarOpen, 
      sidebarExpandedItems, sidebarSearchTerm, groups, communityFilter, selectedGroupId, communityPosts,
      handleCategorySelect, handleDeleteWikiPage, handleFileSelect, handleThreadSelect, handleThreadDelete, handleThreadTitleUpdate,
      handleSidebarToggleExpand, handleCommunityCreateGroup, handleCommunityDiscoverGroups, safeSetActivePage, handleNewChat,
      bookmarks, bookmarkCategories, openBookmarkDialog, handleUpdateBookmarkCategory, handleDeleteBookmarkCategory
  ]);

  const handleBack = useCallback(() => {
    handleNavigate(() => {
        const params = new URLSearchParams(urlSearchParams.toString());
        if (activePage === 'profile') {
            if (params.has('file')) {
                const fileId = params.get('file');
                const file = [...bucketFiles, ...wikiPages].find(f => f.id === fileId);
                params.delete('file');
                params.delete('highlight');

                // If going back from a file in profile, try to return to its context
                if (file?.author === user?.email) {
                    params.set('collection', 'wiki');
                    params.delete('category');
                } else if (params.get('collection') === 'wiki') {
                    // Stay in wiki context if we were there
                } else if (!params.has('category')) {
                    // Default to collection for readings if no category was selected
                    params.set('collection', 'all');
                }
            } else if (params.get('collection') === 'wiki' || params.get('collection') === 'all' || params.has('category')) {
                params.delete('collection');
                params.delete('category');
            }
            const searchStr = params.toString();
            navigateInternal(`/profile${searchStr ? '?' + searchStr : ''}`);
        } else {
            const parentPath = currentPathname.substring(0, currentPathname.lastIndexOf('/'));
            const search = urlSearchParams.toString();
            navigateInternal((parentPath || '/') + (search ? '?' + search : ''));
        }
    });
  }, [handleNavigate, urlSearchParams, activePage, navigateInternal, currentPathname, user, bucketFiles, wikiPages]);

  const renderPageContent = (injectedSidebar?: React.ReactNode) => {
    if (selectedFile && activePage !== 'profile') {
        const isGCSReading = !selectedFile.author && (selectedFile.name.startsWith('Reading ') || selectedFile.id.startsWith('Reading '));
        const articleImages: string[] = [];
        if (selectedFile.content) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(selectedFile.content, 'text/html');
            doc.querySelectorAll('img').forEach(img => {
                const src = img.getAttribute('src');
                if (src && src.startsWith(FIREBASE_STORAGE_BASE_PATH)) { 
                    articleImages.push(src);
                }
            });
            doc.querySelectorAll('.editor-gallery').forEach(galleryDiv => {
                galleryDiv.querySelectorAll('img').forEach(img => {
                    const src = img.getAttribute('src');
                    if (src && src.startsWith(FIREBASE_STORAGE_BASE_PATH)) {
                        articleImages.push(src);
                    }
                });
            });
        }
        if (selectedFile.imageUrl && selectedFile.imageUrl.startsWith(FIREBASE_STORAGE_BASE_PATH)) {
            articleImages.push(selectedFile.imageUrl);
        }

        const uniqueArticleImages = Array.from(new Set(articleImages)); 
        const canEdit = user && (isAdmin || selectedFile.author === user.email) && !isGCSReading;
        
        return (isEditorMode && canEdit) ? (
            <EditorPage 
                key={selectedFile.id}
                ref={editorRef}
                onBack={handleBack}
                onSave={handleSaveEntry}
                allFiles={activePage === 'readings' ? bucketFiles : wikiPages} 
                user={user}
                initialData={{ file: selectedFile, content: "" }} 
                isAdmin={isAdmin}
                onMockUpdate={() => setMockVersion(v => v + 1)}
                onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
                isSidebarOpen={isSidebarOpen}
                sidebar={injectedSidebar}
                onDirtyChange={setIsEditorDirty}
                articleImages={uniqueArticleImages} 
                localDB={localDB.current}
            />
        ) : (
            <ReadingView 
                key={selectedFile.id}
                file={selectedFile} 
                activePage={activePage}
                onBack={handleBack} 
                isEditorMode={false} 
                onSave={handleSaveEntry} 
                allFiles={activePage === 'readings' ? bucketFiles : wikiPages} 
                highlightText={readingHighlight}
                onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
                isSidebarOpen={isSidebarOpen}
                sidebar={sidebarElement}
                showSearch={false}
                bookmarks={bookmarks}
                bookmarkCategories={bookmarkCategories}
                onOpenBookmarkDialog={openBookmarkDialog}
                onDeleteBookmark={handleDeleteBookmark}
            />
        );
    }

    switch (activePage) {
        case 'wiki':
            return (
                <ArchiveGrid 
                    files={wikiPages.filter(f => (activeCategory === 'all' || f.category === activeCategory) && f.name.toLowerCase().includes(wikiSearchTerm.toLowerCase()))} 
                    searchResults={wikiSearchResults} 
                    aiOverview={null} 
                    onFileSelect={handleFileSelect} 
                    title="Akashic Wiki" 
                    bookmarks={bookmarks}
                />
            );
        case 'chat':
            return <ChatPage 
                        key="chat-page-stable"
                        user={user ? { ...user, monthlySpendCap, topupBalance, currentUsage, payAsYouGo } : null} 
                        activeThreadId={activeThreadId} 
                        threads={threads} 
                        onSaveThread={(t) => { 
                            setThreads(prev => [t, ...prev.filter(x => x.id !== t.id)]); 
                            if(user) saveThreadToFirestore(user.uid, t); 
                            // Only navigate if we are on a new chat or the ID changed
                            if (!activeThreadId || activeThreadId !== t.id) {
                                navigateInternal(`/chat/${t.id}`, { replace: !activeThreadId }); 
                            }
                        }} 
                        onNewChat={handleNewChat} 
                        allFiles={[...bucketFiles, ...wikiPages]}
                        onFileSelect={handleFileSelect}
                        initialQuery={chatInitialQuery}
                        localDB={localDB.current}
                        guestUsage={guestUsage}
                        onIncrementGuestUsage={(amount) => setGuestUsage(prev => prev + amount)}
                        onIncrementUsage={handleIncrementUsage}
                        onNavigateToSettings={() => onNavigateToSettings('usage')}
                        aiUsage={aiUsage}
                        onSignIn={handleSignIn}
                        bypassBilling={bypassBilling}
                    />;
        case 'readings':
             return <ReadingsBrowser 
                        allFiles={bucketFiles} 
                        localDB={localDB} 
                        isIndexing={isIndexing} 
                        indexedCount={indexedCount} 
                        memoryUsageMB={memoryUsageMB} 
                        startIndexing={handleStartIndexing} 
                        isLoading={isLoadingBucket} 
                        onFileSelect={handleFileSelect}
                        searchTerm={readingsQuery}
                        activeCategory={activeCategory}
                    />;
        case 'community':
             return isContactView ? 
                <ContactPage initialSubject={contactSubject} autoScroll={forceShowContact} /> : 
                <CommunityPage 
                    user={user} 
                    groups={groups} 
                    setGroups={setGroups} 
                    isEditorMode={isEditorMode} 
                    activeFilter={communityFilter} 
                    selectedGroupId={selectedGroupId} 
                    setPosts={setCommunityPosts}
                    posts={communityPosts}
                    searchTerm={communitySearchTerm}
                    onSavePost={handleSavePost}
                    onDeletePost={handleDeletePost}
                />;
        case 'profile':
        case 'settings':
            const searchParams = urlSearchParams;
            const collectionParam = searchParams.get('collection');
            const categoryParam = searchParams.get('category');
            const fileParam = searchParams.get('file');

            const currentFile = selectedFile;
            if (fileParam && currentFile) {
                const bookmark = bookmarks.find(b => b.fileId === fileParam);
                return (
                    <ReadingView 
                        key={currentFile.id}
                        file={currentFile} 
                        activePage={activePage}
                        onBack={handleBack} 
                        isEditorMode={false} 
                        onSave={handleSaveEntry} 
                        allFiles={[...bucketFiles, ...wikiPages]} 
                        highlightText={readingHighlight}
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        isSidebarOpen={isSidebarOpen}
                        sidebar={sidebarElement}
                        showSearch={false}
                        bookmarks={bookmarks}
                        bookmarkCategories={bookmarkCategories}
                        onOpenBookmarkDialog={openBookmarkDialog}
                        onDeleteBookmark={handleDeleteBookmark}
                        breadcrumbRootLabel={bookmark?.categoryPath || (currentFile.author === user?.email ? 'My Wiki Pages' : 'My Collections')}
                    />
                );
            }

            if (categoryParam) {
                return (
                   <ProfileCategory 
                      categoryPath={categoryParam}
                      bookmarks={bookmarks}
                      onNavigateToBookmark={(bm) => {
                          const file = [...bucketFiles, ...wikiPages].find(f => f.id === bm.fileId);
                          if (file) handleFileSelect(file);
                      }}
                      onDeleteBookmark={handleDeleteBookmark}
                      onBack={handleBack}
                      onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      isSidebarOpen={isSidebarOpen}
                   />
                );
            }

            if (collectionParam === 'wiki') {
                const myWikiFiles = wikiPages.filter(f => f.author === user?.email);
                return (
                    <ProfileWiki 
                        files={myWikiFiles}
                        onFileSelect={handleFileSelect}
                        onBack={handleBack}
                    />
                );
            }

            if (collectionParam === 'all') {
                return (
                    <ProfileCollection 
                        bookmarks={bookmarks}
                        categories={bookmarkCategories}
                        onCategorySelect={handleCategorySelect}
                        onBack={handleBack}
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        isSidebarOpen={isSidebarOpen}
                    />
                );
            }

            if (!user) {
                return (
                    <div className="flex flex-col items-center justify-center h-full p-6 text-center space-y-6">
                        <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                            <i className="fa-solid fa-lock text-amber-500 text-3xl"></i>
                        </div>
                        <div>
                            <h2 className="text-2xl font-serif font-bold text-stone-800 dark:text-stone-100 mb-2">Account Required</h2>
                            <p className="text-stone-500 dark:text-stone-400 max-w-xs mx-auto">Please sign in to access your account settings and manage your spending cap.</p>
                        </div>
                        <button 
                            onClick={handleSignIn}
                            className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold shadow-lg shadow-amber-500/20 transition-all flex items-center gap-2"
                        >
                            <i className="fa-solid fa-right-to-bracket"></i> Sign In to Account
                        </button>
                    </div>
                );
            }

            return (
                <SettingsPage 
                    user={{ ...user, monthlySpendCap, topupBalance, currentUsage }} 
                    aiUsage={aiUsage}
                    theme={theme} 
                    fontSize={fontSize} 
                    onThemeChange={handleThemeChange} 
                    onFontSizeChange={handleFontSizeChange} 
                    onSpendCapChange={handleSpendCapChange}
                    payAsYouGo={payAsYouGo}
                    onPayAsYouGoChange={handlePayAsYouGoChange}
                    quotaAlertThreshold={quotaAlertThreshold}
                    onQuotaAlertThresholdChange={handleQuotaAlertThresholdChange}
                    isEditorMode={isEditorMode}
                    bypassBilling={bypassBilling}
                    onBypassBillingChange={handleBypassBillingChange}
                    onClose={() => navigateInternal('/')} 
                    onSignOut={handleSignOut}
                    onChangePassword={() => {
                        setAuthModalMode('directChange');
                        setIsAuthModalOpen(true);
                    }}
                    onUpdateProfile={async (updates: any) => {
                        if (!user) return;
                        try {
                            const { updateProfile: firebaseUpdateProfile, auth } = await import('./services/auth');
                            if (auth && auth.currentUser) {
                                // firebaseUpdateProfile expects standard photoURL/displayName, so conditionally call it
                                if (updates.displayName || updates.photoURL) {
                                  await firebaseUpdateProfile(auth.currentUser, updates);
                                }
                                setUser(prev => {
                                    if (!prev) return null;
                                    const next = { ...prev };
                                    if (updates.displayName) next.name = updates.displayName;
                                    if (updates.photoURL) next.picture = updates.photoURL;
                                    if (updates.notifications) next.notifications = updates.notifications;
                                    return next;
                                });
                                if (updates.notifications) {
                                    saveUserSettings(user.uid, { notifications: updates.notifications });
                                }
                            }
                        } catch (err) {
                            console.error("Profile update error:", err);
                        }
                    }}
                    bookmarks={bookmarks}
                    bookmarkCategories={bookmarkCategories}
                    payments={payments}
                    onNavigateToBookmark={handleNavigateToBookmark}
                    onDeleteBookmark={handleDeleteBookmark}
                    languages={LANGUAGES}
                    currentLanguageCode={currentLangCode}
                    onLanguageChange={handleLanguageSelect}
                    onPaymentMethodUpdate={(method) => {
                        if (user) {
                            const hasMethod = method !== null;
                            setUser(prev => {
                                if (!prev) return null;
                                return { ...prev, primaryPaymentMethod: method, hasPaymentMethod: hasMethod };
                            });
                            saveUserSettings(user.uid, { primaryPaymentMethod: method, hasPaymentMethod: hasMethod })
                                .then(success => {
                                    if (success) {
                                        console.log("Payment settings updated successfully in Firestore");
                                    } else {
                                        console.error("Failed to update payment settings in Firestore");
                                    }
                                })
                                .catch(err => console.error("Payment update error:", err));
                        }
                    }}
                    onTopup={handleTopup}
                    hash={location.hash}
                />
            );
        case 'legacy':
            return (
                <LegacyPage 
                    isEditorMode={isEditorMode} 
                    onSave={handleSavePageContent} 
                    content={pageContent} 
                    wikiArticles={wikiPages}
                    onSearchReadings={handleReadingsSearch}
                    onSearchChat={handleChatStart}
                    onNavigateToCategory={handleCategorySelect}
                    onNavigateToContact={onNavigateToContact}
                    onNavigateToArticle={(id) => navigateInternal(`/wiki?file=${encodeURIComponent(id)}`)}
                />
            );
        default: return null;
    }
  };

  return (
    <div className={`flex flex-col h-full w-full bg-[#e6dec6] dark:bg-stone-900 text-stone-800 dark:text-stone-300 overflow-hidden font-sans relative transition-colors duration-300 ${fontSize === 'sm' ? 'text-sm' : fontSize === 'lg' ? 'text-lg' : 'text-base'}`}>
      
      {authError && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-stone-900/80 backdrop-blur-sm pt-0 px-4 pb-4">
            <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-red-200 dark:border-red-900/30 animate-in fade-in zoom-in duration-300">
                <div className="bg-red-500 p-6 text-white flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <i className="fa-solid fa-shield-halved text-3xl"></i>
                    </div>
                    <h2 className="text-xl font-bold">Authentication Error</h2>
                </div>
                <div className="p-8 space-y-6">
                    <div className="space-y-2">
                        <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed">
                            {authError.message}
                        </p>
                        {authError.domain && (
                            <div className="mt-4 p-4 bg-stone-50 dark:bg-stone-800/50 rounded-xl border border-stone-200 dark:border-stone-700 space-y-3">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Domain to Authorize</p>
                                <div className="flex items-center justify-between gap-2">
                                    <code className="text-xs font-mono text-amber-600 dark:text-amber-400 break-all">{authError.domain}</code>
                                    <button 
                                        onClick={() => {
                                            navigator.clipboard.writeText(authError.domain!);
                                            const btn = document.getElementById('copy-domain-btn');
                                            if (btn) {
                                                const original = btn.innerHTML;
                                                btn.innerHTML = '<i class="fa-solid fa-check"></i>';
                                                setTimeout(() => btn.innerHTML = original, 2000);
                                            }
                                        }}
                                        id="copy-domain-btn"
                                        className="p-2 text-stone-400 hover:text-amber-500 transition-colors"
                                        title="Copy to clipboard"
                                    >
                                        <i className="fa-solid fa-copy"></i>
                                    </button>
                                </div>
                                <div className="p-3 bg-white dark:bg-stone-900 rounded-lg text-[11px] leading-relaxed shadow-sm space-y-2">
                                    {authError.type === 'firebase' ? (
                                        <>
                                            <p className="font-bold text-stone-700 dark:text-stone-300">How to fix in Firebase Console:</p>
                                            <ol className="list-decimal pl-4 space-y-1">
                                                <li>Open your Firebase Console</li>
                                                <li>Select your project</li>
                                                <li>Go to <strong>Authentication</strong> &gt; <strong>Settings</strong> &gt; <strong>Authorized domains</strong></li>
                                                <li>Click <strong>Add domain</strong></li>
                                                <li>Paste the exact domain above (no https:// or trailing slash)</li>
                                            </ol>
                                        </>
                                    ) : authError.type === 'gcp' ? (
                                        <>
                                            <p className="font-bold text-stone-700 dark:text-stone-300">How to fix in Google Cloud:</p>
                                            <ol className="list-decimal pl-4 space-y-1">
                                                <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-amber-600 hover:underline">Google Cloud Console</a></li>
                                                <li>Select your project</li>
                                                <li>Click on your <strong>API Key</strong> (usually "Browser key (auto created by Firebase)")</li>
                                                <li>Under <strong>Application restrictions</strong>, ensure it's set to "None" OR "HTTP referrers"</li>
                                                <li>If using "HTTP referrers", add <code className="bg-stone-200 dark:bg-stone-700 px-1 rounded">{authError.domain}</code> to the list</li>
                                            </ol>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-4 pt-2">
                        <div className="text-xs text-stone-500 dark:text-stone-500 space-y-2">
                            <p className="font-bold uppercase tracking-tight">How to fix:</p>
                            <ol className="list-decimal list-inside space-y-1 ml-1">
                                <li>Open your Firebase Console</li>
                                <li>Go to <b>Authentication</b> &gt; <b>Settings</b></li>
                                <li>Add the domain above to <b>Authorized domains</b></li>
                            </ol>
                        </div>
                        
                        <button 
                            onClick={() => setAuthError(null)}
                            className="w-full py-3 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-300 rounded-xl font-bold transition-all"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}

      {urlError && (
          <div className="flex items-center justify-center h-full w-full bg-[#e6dec6] dark:bg-stone-900 transition-colors fixed inset-0 z-50">
             <div className="flex flex-col items-center gap-6 text-center max-w-md p-8 animate-in fade-in zoom-in-95 duration-500">
                <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center text-red-500 mb-2">
                    <i className="fa-solid fa-file-circle-xmark text-4xl"></i>
                </div>
                <div>
                    <h3 className="text-2xl font-serif font-bold text-stone-800 dark:text-stone-100 mb-2">Record Not Found</h3>
                    <p className="text-stone-500 dark:text-stone-400 text-sm">
                        The requested record <span className="font-mono bg-stone-200 dark:bg-stone-800 px-1 rounded text-stone-800 dark:text-stone-200">{urlError}</span> could not be located in the archives.
                    </p>
                </div>
                <button 
                    onClick={() => navigateInternal('/')}
                    className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-colors"
                >
                    Go Home
                </button>
             </div>
         </div>
      )}
      
      {showIOSInstallModal && (
        <IOSInstallPrompt onClose={() => setShowIOSInstallModal(false)} />
      )}

      {showUnsavedDialog && (
          <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center pt-0 px-4 pb-4">
              <div className="bg-white dark:bg-stone-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-200 border border-stone-200 dark:border-stone-700">
                  <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="fa-solid fa-floppy-disk text-amber-500 text-2xl"></i>
                      </div>
                      <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-white mb-2">Unsaved Changes</h3>
                      <p className="text-sm text-stone-500 dark:text-stone-400">
                          You have unsaved changes in your article. Do you want to save them before leaving?
                      </p>
                  </div>
                  <div className="flex flex-col gap-3">
                      <button 
                          onClick={handleConfirmSave}
                          className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2"
                      >
                          <i className="fa-solid fa-check"></i> Save & Continue
                      </button>
                      <button 
                          onClick={handleConfirmDiscard}
                          className="w-full py-3 bg-stone-100 dark:bg-stone-700 hover:bg-red-50 dark:hover:bg-red-900/20 text-stone-600 dark:text-stone-300 hover:text-red-600 dark:hover:text-red-400 rounded-xl font-bold transition-colors"
                      >
                          Discard Changes
                      </button>
                      <button 
                          onClick={() => { setShowUnsavedDialog(false); setPendingNavigation(null); }}
                          className="w-full py-2 text-stone-400 hover:text-stone-600 text-xs font-bold uppercase tracking-wider transition-colors"
                      >
                          Cancel
                      </button>
                  </div>
              </div>
          </div>
      )}

      {isLogoModalOpen && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center pt-0 px-4 pb-4" onClick={() => !logoUploading && setIsLogoModalOpen(false)}>
            <div className="bg-white dark:bg-stone-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-stone-100 dark:border-stone-700 flex justify-between items-center bg-stone-50 dark:bg-stone-900/50">
                    <h3 className="font-bold text-stone-800 dark:text-stone-200 flex items-center gap-2">
                        <i className="fa-solid fa-camera text-amber-500"></i> Change App Logo
                    </h3>
                    {!logoUploading && (
                        <button onClick={() => setIsLogoModalOpen(false)} className="text-stone-400 hover:text-stone-600">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    )}
                </div>
                <div className="p-6 space-y-6">
                    <div>
                        <label className="text-xs font-bold text-stone-400 uppercase tracking-wider block mb-2">Select from Default Icons</label>
                        <div className="grid grid-cols-4 gap-2">
                            {[
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/logo.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/icon_book.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/icon_eye.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/icon_sun.webp`
                            ].map((path, idx) => (
                                <button 
                                    key={idx} 
                                    onClick={() => handleLogoAssetSelect(path)} 
                                    className="relative aspect-square rounded-lg overflow-hidden border border-stone-200 dark:border-stone-700 hover:border-amber-500 transition-all group bg-stone-100 dark:bg-stone-900"
                                >
                                    <img 
                                        src={getResizedImageUrl(path, '300x300')} 
                                        className="w-full h-full object-cover" 
                                        alt="Asset" 
                                        onError={(e) => e.currentTarget.style.display = 'none'}
                                    />
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] truncate px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {path.split('/').pop()}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                        <span className="text-xs font-bold text-stone-400">OR</span>
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">Upload from device</label>
                        <button 
                            onClick={() => logoFileInputRef.current?.click()}
                            disabled={logoUploading}
                            className={`mt-2 w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold border transition-all relative overflow-hidden ${
                                logoUploading 
                                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-700 dark:text-amber-400' 
                                : 'bg-stone-100 dark:bg-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-600'
                            }`}
                        >
                            <i className={`fa-solid ${logoUploading ? 'fa-circle-notch animate-spin' : 'fa-upload'}`}></i>
                            {logoUploading ? `Uploading...` : 'Choose an image...'}
                        </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                        <span className="text-xs font-bold text-stone-400">OR</span>
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">Paste an image link</label>
                        <div className="mt-2 flex gap-2">
                            <input 
                                type="text" 
                                value={logoMediaUrlInput}
                                onChange={(e) => setLogoMediaUrlInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleLogoUrlApply()}
                                placeholder="Image URL..."
                                disabled={logoUploading}
                                className="flex-1 bg-stone-100 dark:bg-stone-900 border border-stone-200 dark:border-stone-600 rounded-lg p-3 text-sm focus:border-amber-500 focus:ring-amber-500 outline-none text-stone-800 dark:text-stone-200"
                            />
                            <button 
                                onClick={handleLogoUrlApply}
                                disabled={logoUploading}
                                className="px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold shadow-sm transition-colors disabled:opacity-50"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      )}

      <input type="file" ref={logoFileInputRef} onChange={handleLogoFileUpload} accept="image/*" className="hidden" />

      <header className="hidden md:flex bg-[#f2efe4] dark:bg-[#0f1521] border-b border-stone-200 dark:border-[#1e293b] px-4 items-center justify-between h-16 shrink-0 relative z-[100] shadow-md">
        <div className="flex items-center gap-6">
            <div className="relative group">
                <button onClick={() => navigateInternal('/')} className="w-10 h-10 rounded-full overflow-hidden border-2 border-stone-300 hover:border-amber-500 transition-colors bg-white relative">
                    {!isPageContentLoaded ? (
                        <div className="absolute inset-0 bg-stone-200 dark:bg-stone-800 animate-pulse" />
                    ) : (
                        <img src={getResizedImageUrl(pageContent['app_logo'], '300x300') || ''} className="w-full h-full object-cover animate-in fade-in duration-500" alt="Logo" />
                    )}
                </button>
                {isEditorMode && isPageContentLoaded && (
                    <button 
                        onClick={() => setIsLogoModalOpen(true)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-full text-white transition-opacity"
                        title="Change Logo"
                    >
                        <i className="fa-solid fa-camera text-xs"></i>
                    </button>
                )}
            </div>
            
            <nav className="flex items-center gap-6 text-sm font-serif font-semibold">
                <button onClick={() => safeSetActivePage('legacy')} className={`transition-colors ${activePage === 'legacy' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-100'}`}>Legacy</button>
                <button onClick={() => safeSetActivePage('readings')} className={`transition-colors ${activePage === 'readings' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-100'}`}>Readings</button>
                <button onClick={() => safeSetActivePage('chat')} className={`transition-colors ${activePage === 'chat' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-100'}`}>AI Chat</button>
                <button onClick={() => safeSetActivePage('wiki')} className={`transition-colors ${activePage === 'wiki' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-100'}`}>Akashic Wiki</button>
                <button onClick={() => safeSetActivePage('community')} className={`transition-colors ${activePage === 'community' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-100'}`}>Community</button>
            </nav>
        </div>

        <div className="flex items-center gap-4">
            {renderLanguageSelector()}
            <div id="google_translate_element_container" className="google-translate-native-container !hidden"></div>
            {isAdmin && (
                <div className="flex items-center gap-4">
                    {isEditorMode && activePage === 'legacy' && (
                        <button onClick={handleGlobalMockUpdate} className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors shadow-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:border-amber-500 hover:text-amber-500" title="Confirm Update">
                            <i className="fa-solid fa-check"></i>
                            <span>Update Assets</span>
                        </button>
                    )}
                    {isEditorMode && activePage !== 'legacy' && selectedFile && (
                        <button onClick={handleGlobalMockUpdate} className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors shadow-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:border-amber-500 hover:text-amber-500" title="Export Mock Data">
                            <i className="fa-solid fa-code-branch"></i>
                            <span>Update Mocks</span>
                        </button>
                    )}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-amber-500">Editor</span>
                        <button 
                            onClick={() => {
                                if (selectedFile && !user) {
                                    alert("Please sign in to edit documents.");
                                    return;
                                }
                                if (selectedFile) {
                                    const isGCSReading = selectedFile.name.startsWith('Reading ') || selectedFile.id.startsWith('Reading ');
                                    if (isGCSReading) {
                                        alert("Archival Readings are read-only and cannot be edited.");
                                        return;
                                    }
                                    const canEdit = isAdmin || selectedFile.author === user?.email;
                                    if (!canEdit) {
                                        alert("You do not have permission to edit this document.");
                                        return;
                                    }
                                }
                                setIsEditorMode(!isEditorMode);
                            }} 
                            className={`w-11 h-6 rounded-full relative transition-colors ${isEditorMode ? 'bg-amber-500' : 'bg-stone-300 dark:bg-stone-700'}`}
                        >
                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${isEditorMode ? 'left-6' : 'left-1'}`}></div>
                        </button>
                    </div>
                </div>
            )}
            <button onClick={() => { if (user) { navigateInternal('/profile'); } else { handleSignIn(); } }} className="flex items-center gap-2 group">
                 {user ? <img src={user.picture} className="w-8 h-8 rounded-full border-2 border-transparent group-hover:border-amber-500 transition-all shadow-sm" alt="User" /> : <span className="text-xs font-bold bg-amber-500 text-white px-4 py-2 rounded-full shadow-sm hover:bg-amber-600 transition-colors">Sign in</span>}
            </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col overflow-hidden relative">
          
          {activePage !== 'legacy' && !selectedFile && !isContactView && (
              <>
                {activePage === 'wiki' && (
                    <WikiHeader 
                        title={activeCategory === 'all' ? 'All Contents' : activeCategory} 
                        breadcrumbRootLabel="Wiki" 
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                        isSidebarOpen={isSidebarOpen} 
                        showSearch={true} 
                        onSearchChange={setWikiSearchTerm} 
                        searchQuery={wikiSearchTerm} 
                        languageSelector={renderLanguageSelector()}
                    />
                )}
                {activePage === 'chat' && (
                    <WikiHeader 
                        title="AI Consultant" 
                        breadcrumbRootLabel="Consultation" 
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                        isSidebarOpen={isSidebarOpen}
                        languageSelector={renderLanguageSelector()}
                        rightAction={
                            <button 
                                onClick={handleNewChat} 
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-colors shadow-sm"
                                title="Start New Chat"
                            >
                                <i className="fa-solid fa-plus text-sm"></i>
                            </button>
                        }
                    />
                )}
                {activePage === 'readings' && (
                    <WikiHeader 
                        title="Readings Browser" 
                        breadcrumbRootLabel="Archive" 
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                        isSidebarOpen={isSidebarOpen} 
                        languageSelector={renderLanguageSelector()}
                        centerContent={
                            <ReadingsHeaderControls 
                                searchTerm={readingsQuery}
                                onSearchChange={handleReadingsSearch}
                                isIndexing={isIndexing}
                                indexedCount={indexedCount}
                                totalCount={bucketFiles.length}
                            />
                        }
                    />
                )}
                {activePage === 'community' && (
                    <WikiHeader 
                        title="Community" 
                        breadcrumbRootLabel="Social" 
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                        isSidebarOpen={isSidebarOpen} 
                        showSearch={true} 
                        searchQuery={communitySearchTerm}
                        onSearchChange={setCommunitySearchTerm}
                        searchPlaceholder="Search discussions..."
                        languageSelector={renderLanguageSelector()}
                    />
                )}
                {(activePage === 'profile' || activePage === 'settings') && user && (
                    <WikiHeader 
                        title={urlSearchParams.has('category') ? (urlSearchParams.get('category')?.split('/').pop() || '') : (urlSearchParams.get('collection') === 'wiki' ? 'My Wiki Pages' : (urlSearchParams.get('collection') === 'all' ? 'My Collections' : 'Account'))} 
                        breadcrumbRootLabel={urlSearchParams.has('category') ? "My Collections" : (urlSearchParams.get('collection') === 'wiki' || urlSearchParams.get('collection') === 'all' ? "Account Settings" : "Profile")} 
                        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                        onBack={(urlSearchParams.has('category') || urlSearchParams.get('collection') === 'wiki' || urlSearchParams.get('collection') === 'all') ? handleBack : undefined}
                        isSidebarOpen={isSidebarOpen} 
                        languageSelector={renderLanguageSelector()}
                    />
                )}
              </>
          )}

          <div className="flex-1 flex overflow-hidden relative">
            {showSidebar && !selectedFile && sidebarElement}
            
            <div className={`flex-1 overflow-hidden relative ${selectedFile ? 'flex flex-col' : ''}`}>
                {renderPageContent(selectedFile ? sidebarElement : undefined)}
            </div>
          </div>
      </main>

      <nav className="md:hidden bg-[#f2efe4] dark:bg-[#0f1521] border-t border-stone-200 dark:border-[#1e293b] flex items-center justify-between px-2 h-16 shrink-0 z-[60] pb-safe transition-colors duration-300">
        
        <button onClick={() => safeSetActivePage('readings')} className={`flex-1 flex flex-col items-center justify-center p-2 transition-colors ${activePage === 'readings' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-300'}`}>
          <i className="fa-solid fa-scroll text-xl"></i>
        </button>
        <button onClick={() => safeSetActivePage('chat')} className={`flex-1 flex flex-col items-center justify-center p-2 transition-colors ${activePage === 'chat' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-300'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path></svg>
        </button>

        <button onClick={() => safeSetActivePage('wiki')} className={`flex-1 flex flex-col items-center justify-center p-2 transition-colors ${activePage === 'wiki' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-300'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><circle cx="12" cy="12" r="3.5"></circle><circle cx="12" cy="5" r="3.5"></circle><circle cx="12" cy="19" r="3.5"></circle><circle cx="6.05" cy="8.5" r="3.5"></circle><circle cx="17.95" cy="8.5" r="3.5"></circle><circle cx="6.05" cy="15.5" r="3.5"></circle><circle cx="17.95" cy="15.5" r="3.5"></circle></svg>
        </button>
        <button onClick={() => safeSetActivePage('community')} className={`flex-1 flex flex-col items-center justify-center p-2 transition-colors ${activePage === 'community' ? 'text-amber-500' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-300'}`}>
          <i className="fa-solid fa-users text-xl"></i>
        </button>
        <button onClick={() => { if (user) { navigateInternal('/profile'); } else { handleSignIn(); } }} className="flex-1 flex flex-col items-center justify-center p-2 transition-all">
          <div className={`w-9 h-9 rounded-full overflow-hidden border-2 flex items-center justify-center bg-[#f2efe4] dark:bg-stone-800 ${(activePage === 'profile' || activePage === 'settings') ? 'border-amber-500' : 'border-stone-300 dark:border-stone-600'}`}>
            {user ? <img src={user.picture} className="w-full h-full object-cover" alt="User" /> : <i className="fa-solid fa-user text-stone-400 text-sm"></i>}
          </div>
        </button>
      </nav>

      <BookmarkDialog 
          isOpen={bookmarkDialogState.isOpen}
          onClose={() => setBookmarkDialogState(prev => ({ ...prev, isOpen: false }))}
          onSave={handleSaveBookmark}
          onDelete={handleDeleteBookmark}
          bookmarks={bookmarks}
          categories={bookmarkCategories}
          onAddCategory={handleAddBookmarkCategory}
          onUpdateCategory={handleUpdateBookmarkCategory}
          onDeleteCategory={handleDeleteBookmarkCategory}
          initialTitle={bookmarkDialogState.initialTitle}
          fileId={bookmarkDialogState.fileId}
          fileName={bookmarkDialogState.fileName}
          paragraphId={bookmarkDialogState.paragraphId}
          paragraphText={bookmarkDialogState.paragraphText}
          existingBookmark={bookmarkDialogState.existingBookmark}
      />
      <PaymentMethodDialog
          isOpen={isPaymentMethodDialogOpen}
          onClose={handleClosePaymentMethodDialog}
          onNavigateToSettings={onNavigateToSettings}
      />

      {paymentNotice && (
          <div className="fixed bottom-6 right-6 z-[300] animate-in slide-in-from-right duration-300">
              <div className={`px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 ${paymentNotice.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <i className={`fa-solid ${paymentNotice.type === 'success' ? 'fa-check' : 'fa-triangle-exclamation'}`}></i>
                  </div>
                  <div>
                      <p className="font-bold text-sm">{paymentNotice.type === 'success' ? 'Payment Success' : 'Payment Issue'}</p>
                      <p className="text-xs opacity-90">{paymentNotice.message}</p>
                  </div>
                  <button onClick={() => setPaymentNotice(null)} className="ml-2 opacity-60 hover:opacity-100">
                      <i className="fa-solid fa-xmark"></i>
                  </button>
              </div>
          </div>
      )}

      <AuthModal 
          isOpen={isAuthModalOpen}
          onClose={() => {
              setIsAuthModalOpen(false);
              setAuthModalMode('options');
              setAuthResetToken('');
          }}
          initialMode={authModalMode}
          resetToken={authResetToken}
          initialEmail={user?.email || ''}
          userId={user?.uid || ''}
          onSuccess={(firebaseUser) => {
              // The onAuthStateChange listener in useEffect will handle setting the user state
              setIsAuthModalOpen(false);
              setAuthModalMode('options');
              setAuthResetToken('');
          }}
          onError={setAuthError}
      />
    </div>
  );
};

export default App;